/* eslint-disable react/forbid-dom-props */
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Star, Scale, Bot, Search, CheckCircle2, AlertCircle, MessageSquare, TrendingUp, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useFormStore } from '../../../store/useFormStore';
import { contactsApi, customFieldsApi, jurisprudenciasApi, usersApi, formsApi, addressesApi } from '../../../services/api';
import { lookupCep } from '../../../services/cepService';
import { digits, maskCpf, maskCnpj, maskCep, maskPis, maskPhone, maskCei, maskDateBR, normalizeDateBRInput, formatBRL, parseBRL } from '../../../utils/masks';

// ── Constants ────────────────────────────────────────────────
const UF_LIST = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const LOCKED_FIELD_TITLE = 'Campo travado após aprovação da petição';
const citiesByUfCache = new Map();

export const PEDIDOS_DEF = [
  // Rescisório
  { id: 'salario_saldo',           group: 'Rescisório',  label: 'Saldo de salário' },
  { id: 'ferias_proporcionais',    group: 'Rescisório',  label: 'Férias proporcionais + 1/3' },
  { id: 'ferias_vencidas',         group: 'Rescisório',  label: 'Férias vencidas + 1/3' },
  { id: 'decimo_terceiro_prop',    group: 'Rescisório',  label: '13º salário proporcional' },
  { id: 'decimo_terceiro_vencido', group: 'Rescisório',  label: '13º salário vencido' },
  { id: 'aviso_previo',            group: 'Rescisório',  label: 'Aviso prévio indenizado' },
  { id: 'fgts_multa40',            group: 'Rescisório',  label: 'FGTS + multa 40%' },
  { id: 'inss_diferencas',         group: 'Rescisório',  label: 'INSS sobre diferenças salariais' },
  { id: 'multa_477',               group: 'Rescisório',  label: 'Multa do art. 477 da CLT (rescisão atrasada ou não paga)' },
  { id: 'multa_467',               group: 'Rescisório',  label: 'Multa do art. 467 da CLT (verbas incontroversas)' },
  // Contratual
  { id: 'equiparacao_salarial',    group: 'Contratual',  label: 'Equiparação salarial' },
  { id: 'adicional_funcao',        group: 'Contratual',  label: 'Desvio / acúmulo de função' },
  { id: 'diferencas_salariais',    group: 'Contratual',  label: 'Diferenças salariais' },
  // Jornada
  { id: 'horas_extras',            group: 'Jornada',     label: 'Horas extras + reflexos' },
  { id: 'adicional_noturno',       group: 'Jornada',     label: 'Adicional noturno + reflexos' },
  { id: 'hora_percurso',           group: 'Jornada',     label: 'Horas in itinere (percurso)' },
  { id: 'intervalo_intrajornada',  group: 'Jornada',     label: 'Intervalo intrajornada (supressão)' },
  { id: 'feriados_trabalhados',    group: 'Jornada',     label: 'Feriados trabalhados' },
  { id: 'sabados_trabalhados',     group: 'Jornada',     label: 'Sábados trabalhados' },
  // Benefícios
  { id: 'vt_nao_fornecido',        group: 'Benefícios',  label: 'Vale-transporte não fornecido' },
  { id: 'vr_nao_fornecido',        group: 'Benefícios',  label: 'Vale-refeição / cesta básica não fornecida' },
  { id: 'plano_saude_desc',        group: 'Benefícios',  label: 'Plano de saúde (devolução de descontos)' },
  // Especial
  { id: 'insalubridade',           group: 'Especial',    label: 'Adicional de insalubridade' },
  { id: 'periculosidade',          group: 'Especial',    label: 'Adicional de periculosidade' },
  { id: 'indenizacao_acidente',    group: 'Especial',    label: 'Indenização por acidente de trabalho' },
  { id: 'indenizacao_doenca_ocup', group: 'Especial',    label: 'Indenização por doença ocupacional' },
  { id: 'estabilidade',            group: 'Especial',    label: 'Indenização por estabilidade provisória' },
  // Indenização
  { id: 'danos_morais',            group: 'Indenização', label: 'Danos morais' },
  { id: 'danos_morais_assedio',    group: 'Indenização', label: 'Danos morais por assédio moral' },
  { id: 'danos_coacao_rescisao',   group: 'Indenização', label: 'Danos por coação na rescisão' },
  // Honorários
  { id: 'honorarios_adv',          group: 'Honorários',  label: 'Honorários advocatícios sucumbenciais' },
];

// Regime do contrato (1.21). Até a 1.20 toda peça saía CLT urbano de 44 horas, e no caso-piloto
// (doméstica de 30 horas — relatório do Dr. Lucas de 23/09/2026) vieram FGTS + 40%, aviso do
// art. 487 e divisor 220. Rótulos iguais aos de `REGIMES` em backend/app/services/regime_contratual.py.
// Vazio vale CLT urbano (a conta de antes); por isso `clt_urbano` não tem opção própria.
export const REGIMES_CONTRATUAIS = [
  { id: '',              label: 'CLT urbano' },
  { id: 'domestico',     label: 'Empregado doméstico (LC 150/2015)' },
  { id: 'rural',         label: 'Rural (Lei 5.889/73)' },
  { id: 'tempo_parcial', label: 'Tempo parcial (art. 58-A da CLT)' },
  { id: 'intermitente',  label: 'Intermitente (art. 452-A da CLT)' },
  { id: 'aprendiz',      label: 'Aprendiz (art. 428 da CLT)' },
  { id: 'temporario',    label: 'Temporário (Lei 6.019/74)' },
];
// Só CLT urbano e doméstico têm regra própria no cálculo e no texto nesta versão (decisão de
// 23/09/2026). Os demais são reconhecidos, mas a peça sai com a regra da CLT: quem escolhe precisa
// saber que a conta e o texto não se adaptaram sozinhos.
const REGIMES_COM_REGRAS = new Set(['', 'domestico']);

// `clt_urbano` gravado pelo conector e valor desconhecido leem como CLT urbano, como no backend
// (`regime_do_contrato`).
export function regimeDoContrato(contrato) {
  const regime = String(contrato?.regime_contratual || '').trim();
  return REGIMES_CONTRATUAIS.some(r => r.id === regime) ? regime : '';
}

// Doméstica não tem multa de 40%: é o FGTS de 8% mais a indenização compensatória de 3,2% do
// art. 22 da LC 150/2015 (caso-piloto de 23/09/2026). O id continua `fgts_multa40` porque é a
// chave gravada nas fichas e lida pelo cálculo; só o rótulo acompanha o regime do 1º contrato.
export function rotuloDoPedido(pedido, formData) {
  if (pedido?.id === 'fgts_multa40' && regimeDoContrato(formData?.contracts?.[0]) === 'domestico') {
    return 'FGTS 8% + indenização compensatória 3,2% (LC 150)';
  }
  return pedido?.label;
}

// Valor gravado na ficha ("1500.00") ou digitado em pt-BR ("1.500,00") — mesma leitura de
// `numero` em backend/app/services/ficha_esquema.py. `parseBRL` sobre o decimal gravado tirava o
// ponto e lia "1500.00" como 150000.
function valorDecimal(valor) {
  const texto = String(valor ?? '').trim();
  const lido = parseFloat(texto.includes(',') ? parseBRL(texto) : texto);
  return Number.isFinite(lido) ? lido : 0;
}

function getSuggestedIds(fd) {
  const ids = new Set();
  const ct = fd.contracts?.[0];
  const isSemJusta = ct?.dismissal_type === 'sem_justa_causa';
  const isIndireta = ct?.dismissal_type === 'rescisao_indireta';
  if (ct) {
    ids.add('salario_saldo');
    ids.add('ferias_proporcionais');
    ids.add('decimo_terceiro_prop');
    // INSS sobre diferenças só faz sentido quando há salário fora da folha ou vínculo sem
    // registro — sugerido em todo contrato, virou pedido "inventado" na peça da Rita (15/09/2026).
    if (Number(parseBRL(ct.off_payroll_salary || '0')) > 0 || ct.registered_in_ctps === false) ids.add('inss_diferencas');
    if (Number(fd.ferias_vencidas_nao_gozadas) > 0) ids.add('ferias_vencidas');
    if (ct.termination_date) ids.add('decimo_terceiro_vencido');
    // Multas dos arts. 477 e 467: rescisão não paga (valor zero) ou paga mais de 10 dias depois.
    const naoPaga = /^0+([.,]0+)?$/.test(String(ct.severance_amount ?? '').trim());
    const diasAteOPagamento = ct.termination_date && ct.severance_payment_date
      ? (new Date(ct.severance_payment_date) - new Date(ct.termination_date)) / 86400000
      : null;
    if (ct.termination_date && (naoPaga || (diasAteOPagamento !== null && diasAteOPagamento > 10))) ids.add('multa_477');
    // Rescisão indireta, justa causa (reversão) e coação: as verbas são controvertidas e o 467 é
    // decisão do escritório caso a caso — o cálculo também só o faz com a marca ligada.
    const contestado = isIndireta || ct.dismissal_type === 'justa_causa' || fd.coercion_at_dismissal;
    if (ct.termination_date && naoPaga && !contestado) ids.add('multa_467');
    if (!ct.transport_benefit) ids.add('vt_nao_fornecido');
    if (!ct.food_benefit && !ct.meal_benefit) ids.add('vr_nao_fornecido');
  }
  if (isSemJusta || isIndireta) { ids.add('aviso_previo'); ids.add('fgts_multa40'); }
  if (fd.overtime_frequency) ids.add('horas_extras');
  if (fd.night_shift) ids.add('adicional_noturno');
  if (Number(fd.commute_minutes) > 0 || Object.values(fd.work_day_schedules || {}).some(day => Number(day.commute_minutes) > 0)) ids.add('hora_percurso');
  const intervalos = splitDurationRange(fd.intrajornada_interval_minutes).map(Number);
  if (intervalos.length && Math.min(...intervalos) < 60) ids.add('intervalo_intrajornada');
  if (fd.worked_holidays) ids.add('feriados_trabalhados');
  if (fd.saturday_work) ids.add('sabados_trabalhados');
  if (fd.insalubrity) ids.add('insalubridade');
  if (fd.hazard_pay) ids.add('periculosidade');
  if (fd.accidents?.length) { ids.add('indenizacao_acidente'); ids.add('danos_morais'); }
  if (fd.occupational_disease) { ids.add('indenizacao_doenca_ocup'); ids.add('danos_morais'); }
  if (fd.stability_reason) ids.add('estabilidade');
  if (fd.moral_harassment) { ids.add('danos_morais'); ids.add('danos_morais_assedio'); }
  if (fd.coercion_at_dismissal) ids.add('danos_coacao_rescisao');
  ids.add('honorarios_adv');
  return ids;
}

// ── Primitivos reutilizáveis ─────────────────────────────────
function isFilledValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return value === true;
  if (Array.isArray(value)) return value.some(isFilledValue);
  if (typeof value === 'object') return Object.values(value).some(isFilledValue);
  return true;
}

function useField(field) {
  const { formData, setField, lockMode } = useFormStore();
  const value = formData[field] ?? '';
  const isOperational = [
    'documentos_faltantes',
    'general_observations',
    'ai_instructions',
    'master_prompt',
    'ai_hint_identificacao',
    'ai_hint_reclamante',
    'ai_hint_reclamado',
    'ai_hint_contrato',
    'ai_hint_jornada',
    'ai_hint_fatos',
    'ai_hint_juridico',
  ].includes(field);
  return [value, (v) => setField(field, v), !isOperational && lockMode && isFilledValue(value)];
}

function FG({ label, req, opt, hint, span, children }) {
  return (
    <div className={`form-group${span ? ` span-${span}` : ''}`}>
      <label>{label}{req && <span className="req">*</span>}{opt && <span className="opt">(opcional)</span>}</label>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

function lockedProps(locked, disabled, title) {
  return { disabled: disabled || locked, title: locked ? LOCKED_FIELD_TITLE : title };
}

function normalizeLookupText(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function hasAddressNumber(value) {
  const text = String(value || '').toLowerCase();
  return /\d/.test(text) || /\bs\s*\/?\s*n\b/.test(text);
}

async function fetchCitiesByUf(uf) {
  const state = String(uf || '').toUpperCase();
  if (citiesByUfCache.has(state)) return citiesByUfCache.get(state);

  const cities = await addressesApi.cities(state);
  citiesByUfCache.set(state, cities);
  return cities;
}

function TxtInput({ field, mono, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <input type="text" value={v} onChange={e => set(e.target.value)} className={mono ? 'mono-input' : ''} {...lockedProps(locked, disabled, title)} {...p} />;
}
function NumInput({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <input type="number" value={v} onChange={e => set(e.target.value)} {...lockedProps(locked, disabled, title)} {...p} />;
}
function DateInputRaw({ value, onChange, className = '', ...p }) {
  return (
    <input
      type="text"
      value={maskDateBR(value)}
      onChange={e => onChange(normalizeDateBRInput(e.target.value))}
      placeholder="dd/mm/aaaa"
      inputMode="numeric"
      maxLength={10}
      className={`mono-input${className ? ` ${className}` : ''}`}
      {...p}
    />
  );
}
function DateInput({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <DateInputRaw value={v} onChange={set} disabled={disabled || locked} title={locked ? LOCKED_FIELD_TITLE : title} {...p} />;
}
function TimeInput({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <input type="time" value={v} onChange={e => set(e.target.value)} {...lockedProps(locked, disabled, title)} {...p} />;
}
function splitRange(value, pattern) {
  return String(value ?? '').match(pattern)?.slice(0, 2) || [];
}
function splitTimeRange(value) {
  return splitRange(value, /\d{1,2}[:hH]\d{2}/g).map(part => part.replace(/[hH]/, ':').padStart(5, '0'));
}
function splitDurationRange(value) {
  return splitRange(value, /\d+(?:[.,]\d+)?/g);
}
function joinRange(first, last, separator) {
  return [first, last].filter(Boolean).join(separator);
}
function TimeRangeRaw({ value, onChange, disabled }) {
  const [first = '', last = ''] = splitTimeRange(value);
  return (
    <div className="range-inputs">
      <input type="time" value={first} disabled={disabled}
        onChange={e => onChange(joinRange(e.target.value, last, '/'))} />
      <span>até</span>
      <input type="time" value={last} disabled={disabled} aria-label="Horário máximo"
        onChange={e => onChange(joinRange(first, e.target.value, '/'))} />
    </div>
  );
}
function TimeRangeInput({ field }) {
  const [value, set, locked] = useField(field);
  return <TimeRangeRaw value={value} onChange={set} disabled={locked} />;
}
function durationClock(value) {
  if (value === '' || value == null) return '';
  const minutes = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(minutes) || minutes < 0 || minutes >= 1440) return '';
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.round(minutes % 60)).padStart(2, '0')}`;
}
function durationMinutes(value) {
  if (!value) return '';
  const [hours, minutes] = value.split(':').map(Number);
  return String(hours * 60 + minutes);
}
function DurationRangeRaw({ value, onChange, disabled }) {
  const [first = '', last = ''] = splitDurationRange(value);
  return (
    <div className="range-inputs duration-inputs">
      <input type="time" value={durationClock(first)} disabled={disabled}
        aria-label="Duração mínima em horas e minutos"
        onChange={e => onChange(joinRange(durationMinutes(e.target.value), last, ' a '))} />
      <span>até</span>
      <input type="time" value={durationClock(last)} disabled={disabled}
        aria-label="Duração máxima em horas e minutos"
        onChange={e => onChange(joinRange(first, durationMinutes(e.target.value), ' a '))} />
    </div>
  );
}
function DurationRangeInput({ field }) {
  const [value, set, locked] = useField(field);
  return <DurationRangeRaw value={value} onChange={set} disabled={locked} />;
}
function Sel({ field, children, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <select value={v} onChange={e => set(e.target.value)} {...lockedProps(locked, disabled, title)} {...p}>{children}</select>;
}
function LawyerSelect({ field = 'responsible_lawyer_id', disabled, title }) {
  const [v, set, locked] = useField(field);

  const { data: lawyers = [] } = useQuery({
    queryKey: ['lawyers-select'],
    queryFn: usersApi.listLawyers,
    staleTime: 60000,
  });

  return (
    <select
      value={v || ''}
      onChange={e => set(e.target.value || '')}
      disabled={disabled || locked}
      title={locked ? LOCKED_FIELD_TITLE : title}
    >
      <option value="">Selecionar advogado...</option>
      {lawyers.map(l => {
        const oab = l.oab_sp || l.oab_mg || l.oab;
        return (
          <option key={l.id} value={l.id}>
            {l.signature_name || l.name}{oab ? ` (${oab})` : ''}
          </option>
        );
      })}
    </select>
  );
}
function TA({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <textarea value={v} onChange={e => set(e.target.value)} {...lockedProps(locked, disabled, title)} {...p} />;
}
function Check({ field, label, sub }) {
  const { formData, setField, lockMode } = useFormStore();
  const locked = lockMode && isFilledValue(formData[field]);
  return (
    <label className="check-group" title={locked ? LOCKED_FIELD_TITLE : undefined}>
      <input type="checkbox" checked={!!formData[field]} disabled={locked} onChange={e => setField(field, e.target.checked)} />
      <span className="check-label">{label}{sub && <span className="check-sub">{sub}</span>}</span>
    </label>
  );
}
function UFSel({ field }) {
  return (
    <Sel field={field}>
      <option value="">UF</option>
      {UF_LIST.map(u => <option key={u} value={u}>{u}</option>)}
    </Sel>
  );
}
function SepLabel({ children }) {
  return <div className="form-section-label">{children}</div>;
}

export function CustomFieldsBlock({ tab }) {
  const { formData, setField, lockMode } = useFormStore();
  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['custom-fields', 'active'],
    queryFn: () => customFieldsApi.list(false),
  });
  const activeFields = fields.filter(f => f.tab === tab && f.is_active);
  if (isLoading || activeFields.length === 0) return null;

  function setCustom(fieldId, value) {
    setField('custom', { ...(formData.custom || {}), [fieldId]: value });
  }

  function renderInput(field) {
    const value = (formData.custom || {})[field.id] ?? (field.field_type === 'checkbox' ? false : '');
    const locked = lockMode && isFilledValue(value);
    if (field.field_type === 'textarea') {
      return <textarea rows={3} value={value} disabled={locked} title={locked ? LOCKED_FIELD_TITLE : undefined} onChange={e => setCustom(field.id, e.target.value)} />;
    }
    if (field.field_type === 'select') {
      return (
        <select value={value} disabled={locked} title={locked ? LOCKED_FIELD_TITLE : undefined} onChange={e => setCustom(field.id, e.target.value)}>
          <option value="">Selecionar...</option>
          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (field.field_type === 'checkbox') {
      return (
        <label className="check-group" title={locked ? LOCKED_FIELD_TITLE : undefined}>
          <input type="checkbox" checked={!!value} disabled={locked} onChange={e => setCustom(field.id, e.target.checked)} />
          <span className="check-label">Sim</span>
        </label>
      );
    }
    return <input type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} value={value} disabled={locked} title={locked ? LOCKED_FIELD_TITLE : undefined} onChange={e => setCustom(field.id, e.target.value)} />;
  }

  return (
    <div className="form-grid fg-2" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
      <SepLabel>Campos personalizados</SepLabel>
      {activeFields.map(field => (
        <FG key={field.id} label={field.label} req={field.required} span={field.field_type === 'textarea' ? '2' : undefined}>
          {renderInput(field)}
        </FG>
      ))}
    </div>
  );
}

// ── Masked inputs (bound to store field) ────────────────────
function CpfInput({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <input type="text" value={maskCpf(v)} onChange={e => set(digits(e.target.value).slice(0, 11))} className="mono-input" maxLength={14} {...lockedProps(locked, disabled, title)} {...p} />;
}
function PisInput({ field, disabled, title, ...p }) {
  const [v, set, locked] = useField(field);
  return <input type="text" value={maskPis(v)} onChange={e => set(digits(e.target.value).slice(0, 11))} className="mono-input" maxLength={14} {...lockedProps(locked, disabled, title)} {...p} />;
}

// Phone input — standalone (raw digits stored, masked display)
function PhoneInput({ value, onChange, ...p }) {
  return (
    <input
      type="text"
      value={maskPhone(value)}
      onChange={e => onChange(digits(e.target.value).slice(0, 11))}
      className="mono-input"
      maxLength={15}
      {...p}
    />
  );
}

// Currency input — stores decimal string "3405.50", displays "3.405,50"
function CurrencyInputRaw({ value, onChange, ...p }) {
  const [focused, setFocused] = useState(false);
  const [local, setLocal] = useState(() => (value ? formatBRL(value) : ''));

  React.useEffect(() => {
    if (!focused) setLocal(value ? formatBRL(value) : '');
  }, [value, focused]);

  return (
    <input
      type="text"
      value={focused ? local : (value ? formatBRL(value) : '')}
      onFocus={() => { setFocused(true); setLocal(value ? formatBRL(value) : ''); }}
      onBlur={() => setFocused(false)}
      onChange={e => {
        const raw = e.target.value.replace(/[^\d,]/g, '');
        setLocal(raw);
        onChange(parseBRL(raw));
      }}
      className="mono-input"
      {...p}
    />
  );
}
function CurrencyInput({ field, disabled, title, ...p }) {
  const { formData, setField, lockMode } = useFormStore();
  const value = formData[field] ?? '';
  const locked = lockMode && isFilledValue(value);
  return <CurrencyInputRaw value={value} onChange={v => setField(field, v)} disabled={disabled || locked} title={locked ? LOCKED_FIELD_TITLE : title} {...p} />;
}

// CEP lookup field — auto-triggers at 8 digits, manual button fallback
export function CepLookupField({ value, onChange, onFound, disabled }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null | 'ok' | 'err'

  const requestVersion = useRef(0);
  const currentCep = useRef(value);
  currentCep.current = value;
  useEffect(() => () => { requestVersion.current += 1; }, []);

  async function triggerLookup(rawCep) {
    const version = ++requestVersion.current;
    const isCurrent = () => version === requestVersion.current && digits(currentCep.current) === rawCep;
    setLoading(true);
    setStatus(null);
    try {
      const result = await lookupCep(rawCep);
      if (!isCurrent()) return;
      onFound(result);
      if (!result.bairro) toast('CEP sem bairro informado. Confira e preencha o bairro manualmente.');
      setStatus('ok');
    } catch (err) {
      if (!isCurrent()) return;
      setStatus('err');
      toast.error(err.message || 'CEP não encontrado');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }

  function handleChange(e) {
    const raw = digits(e.target.value).slice(0, 8);
    currentCep.current = raw;
    requestVersion.current += 1;
    setLoading(false);
    onChange(raw);
    setStatus(null);
    if (raw.length === 8) triggerLookup(raw);
  }

  async function doLookup() {
    const raw = digits(value);
    if (raw.length !== 8) { toast.error('CEP deve ter 8 dígitos'); return; }
    await triggerLookup(raw);
  }

  return (
    <div style={{ display: 'flex', gap: '.375rem' }}>
      <input
        type="text"
        value={maskCep(value)}
        onChange={handleChange}
        className="mono-input"
        placeholder="00000-000"
        maxLength={9}
        style={{ flex: 1 }}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={doLookup}
        disabled={loading || disabled}
        title="Buscar endereço pelo CEP"
        aria-label="Buscar endereço pelo CEP"
        style={{
          padding: '.375rem .5rem',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg)',
          cursor: loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center',
          color: 'var(--text-secondary)', flexShrink: 0,
        }}
      >
        {loading
          ? <div className="spinner" style={{ width: 13, height: 13 }} />
          : status === 'ok' ? <CheckCircle2 size={13} style={{ color: 'var(--success)' }} />
          : status === 'err' ? <AlertCircle size={13} style={{ color: 'var(--error)' }} />
          : <Search size={13} />}
      </button>
    </div>
  );
}

function PrefillMergeModal({ suggestion, currentFicha, onClose, onConfirm }) {
  const FIELDS_TO_COMPARE = [
    { key: 'claimant_name', label: 'Nome completo' },
    { key: 'claimant_rg', label: 'RG' },
    { key: 'claimant_rg_issuer', label: 'Órgão expedidor' },
    { key: 'claimant_birth_date', label: 'Data de nascimento' },
    { key: 'claimant_mother_name', label: 'Nome da mãe' },
    { key: 'claimant_occupation', label: 'Profissão / ocupação' },
    { key: 'claimant_education', label: 'Escolaridade' },
    { key: 'claimant_ctps_number', label: 'CTPS — Número' },
    { key: 'claimant_ctps_serie', label: 'CTPS — Série' },
    { key: 'claimant_ctps_uf', label: 'CTPS — UF' },
    { key: 'claimant_pis', label: 'PIS / NIT' },
    { key: 'claimant_address', label: 'Endereço completo' },
    { key: 'claimant_neighborhood', label: 'Bairro' },
    { key: 'claimant_city', label: 'Cidade' },
    { key: 'claimant_state', label: 'UF' },
    { key: 'claimant_zip', label: 'CEP' },
    { key: 'claimant_email', label: 'E-mail' },
  ];

  const suggestedClaimant = suggestion.claimant || {};

  const [selectedFields, setSelectedFields] = useState(() => {
    const initial = {};
    FIELDS_TO_COMPARE.forEach(f => {
      const sugVal = suggestedClaimant[f.key];
      const curVal = currentFicha[f.key];

      if (sugVal !== undefined && sugVal !== null && String(sugVal).trim() !== '') {
        if (curVal === undefined || curVal === null || String(curVal).trim() === '') {
          initial[f.key] = true;
        } else {
          initial[f.key] = false;
        }
      } else {
        initial[f.key] = false;
      }
    });

    if (suggestedClaimant.claimant_phones && suggestedClaimant.claimant_phones.length > 0) {
      const curPhones = currentFicha.claimant_phones || [];
      const hasCurPhones = curPhones.some(p => p.number && p.number.trim() !== '');
      initial['claimant_phones'] = !hasCurPhones;
    }
    return initial;
  });

  const toggleField = (key) => {
    setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMerge = () => {
    const keysToMerge = Object.keys(selectedFields).filter(k => selectedFields[k]);
    if (keysToMerge.length === 0) {
      toast.error('Selecione pelo menos um campo para preencher.');
      return;
    }
    onConfirm(keysToMerge);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,35,50,0.5)', zIndex: 2100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }}>
      <div className="card" style={{
        width: '100%', maxWidth: '640px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', padding: '1.25rem', boxShadow: 'var(--shadow-lg)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-light)', paddingBottom: '.5rem', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <Sparkles size={16} color="var(--gold)" />
            Mesclar Dados do Reclamante
          </h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1 }}>×</button>
        </div>

        <p style={{ fontSize: '.75rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Encontramos dados cadastrais deste reclamante na ficha: <strong>{suggestion.source_case_label}</strong>.
          Selecione abaixo quais campos deseja copiar para o formulário atual:
        </p>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1.25rem', paddingRight: '.25rem' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '500px', fontSize: '.75rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-light)' }}>
                  <th style={{ padding: '.5rem', width: '2rem' }}></th>
                  <th style={{ padding: '.5rem', width: '8rem', color: 'var(--text-muted)' }}>Campo</th>
                  <th style={{ padding: '.5rem', color: 'var(--text-muted)' }}>Valor Atual</th>
                  <th style={{ padding: '.5rem', color: 'var(--text-muted)' }}>Sugestão</th>
                </tr>
              </thead>
              <tbody>
                {FIELDS_TO_COMPARE.map(f => {
                  const sugVal = suggestedClaimant[f.key];
                  if (sugVal === undefined || sugVal === null || String(sugVal).trim() === '') return null;

                  const curVal = currentFicha[f.key];
                  const isConflict = curVal !== undefined && curVal !== null && String(curVal).trim() !== '' && String(curVal).trim() !== String(sugVal).trim();

                  return (
                    <tr
                      key={f.key}
                      onClick={() => toggleField(f.key)}
                      style={{
                        borderBottom: '1px solid var(--border-light)',
                        cursor: 'pointer',
                        background: isConflict ? 'rgba(220,53,69,0.03)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '.5rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!selectedFields[f.key]}
                          onChange={() => {}}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '.5rem', fontWeight: 600 }}>{f.label}</td>
                      <td style={{ padding: '.5rem', color: curVal ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {curVal ? String(curVal) : '(vazio)'}
                      </td>
                      <td style={{ padding: '.5rem', color: 'var(--success)', fontWeight: 500 }}>
                        {String(sugVal)}
                        {isConflict && (
                          <span style={{ marginLeft: '.3rem', fontSize: '.6rem', padding: '1px 4px', background: 'var(--error-bg)', color: 'var(--error)', borderRadius: '3px' }}>
                            conflito
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {suggestedClaimant.claimant_phones && suggestedClaimant.claimant_phones.length > 0 && (
                  <tr
                    onClick={() => toggleField('claimant_phones')}
                    style={{
                      borderBottom: '1px solid var(--border-light)',
                      cursor: 'pointer'
                    }}
                  >
                    <td style={{ padding: '.5rem', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedFields['claimant_phones']}
                        onChange={() => {}}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '.5rem', fontWeight: 600 }}>Telefones</td>
                    <td style={{ padding: '.5rem', color: 'var(--text-muted)' }}>
                      {(currentFicha.claimant_phones || []).map(p => p.number).filter(Boolean).join(', ') || '(vazio)'}
                    </td>
                    <td style={{ padding: '.5rem', color: 'var(--success)', fontWeight: 500 }}>
                      {suggestedClaimant.claimant_phones.map(p => p.number).filter(Boolean).join(', ')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={handleMerge}>Aplicar Selecionados</button>
        </div>
      </div>
    </div>
  );
}

function CityAutocompleteField({ value, onChange, state, disabled }) {
  const [query, setQuery] = useState(value || '');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    if (!state || state.length !== 2) {
      setCities([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchCitiesByUf(state)
      .then(data => {
        if (!cancelled) setCities(data);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [state]);

  const term = normalizeLookupText(query);
  const suggestions = focused && term.length >= 2
    ? cities
        .filter(city => normalizeLookupText(city).includes(term))
        .sort((a, b) => {
          const aStarts = normalizeLookupText(a).startsWith(term);
          const bStarts = normalizeLookupText(b).startsWith(term);
          if (aStarts === bStarts) return a.localeCompare(b);
          return aStarts ? -1 : 1;
        })
        .slice(0, 10)
    : [];

  const selectCity = (city) => {
    setQuery(city);
    onChange(city);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          setIsOpen(true);
        }}
        onBlur={() => setTimeout(() => {
          setFocused(false);
          setIsOpen(false);
        }, 200)}
        placeholder={state ? 'Digite a cidade' : 'Selecione a UF primeiro'}
        autoComplete="address-level2"
        disabled={disabled || !state}
      />

      {loading && (
        <div style={{
          position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center'
        }}>
          <div className="spinner" style={{ width: 12, height: 12 }} />
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
          zIndex: 1000, maxHeight: '200px', overflowY: 'auto', marginTop: '4px'
        }}>
          {suggestions.map(city => (
            <div
              key={city}
              onMouseDown={() => selectCity(city)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)', fontSize: '0.75rem',
                color: 'var(--text-primary)', transition: 'background 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(26, 35, 50, 0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddressAutocompleteField({ value, city, state, onChange, onSelect, disabled }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const streetQuery = query.split(',')[0].replace(/\d+/g, '').trim();
    if (!city || !state || streetQuery.length < 3 || !focused) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await addressesApi.search(state, city, streetQuery);
        setSuggestions(data.slice(0, 10));
        setIsOpen(true);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query, city, state, focused]);

  const handleSelect = (item) => {
    if (item.logradouro) onChange(item.logradouro);
    onSelect(item);
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocused(false);
      setIsOpen(false);
    }, 200);
  };

  const hasCityAndState = city.trim() !== '' && state.trim() !== '';
  const missingNumber = query.trim().length > 0 && !hasAddressNumber(query);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        title={missingNumber ? 'Inclua o número no endereço, se houver.' : undefined}
        onChange={e => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => {
          setFocused(true);
          if (suggestions.length > 0) setIsOpen(true);
        }}
        onBlur={handleBlur}
        placeholder="Rua, número, complemento"
        autoComplete="street-address"
        aria-invalid={missingNumber ? 'true' : undefined}
        style={{
          width: '100%',
          borderColor: missingNumber ? 'var(--warning, #b7791f)' : undefined,
        }}
      />

      {loading && (
        <div style={{
          position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', gap: '.25rem'
        }}>
          <div className="spinner" style={{ width: 12, height: 12 }} />
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
          zIndex: 1000, maxHeight: '200px', overflowY: 'auto', marginTop: '4px'
        }}>
          {suggestions.map((item, index) => (
            <div
              key={index}
              onMouseDown={() => handleSelect(item)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                borderBottom: '1px solid var(--border-light)', fontSize: '0.75rem',
                color: 'var(--text-primary)', transition: 'background 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(26, 35, 50, 0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {item.label || `${item.logradouro} - ${item.bairro}, ${item.city}/${item.state}`}
            </div>
          ))}
        </div>
      )}

      {!hasCityAndState && focused && (
        <span style={{ display: 'block', fontSize: '.68rem', color: 'var(--text-muted)', marginTop: '.25rem' }}>
          Preencha UF e Cidade para buscar CEP pelo nome da rua.
        </span>
      )}

      {missingNumber && (
        <span style={{ display: 'block', fontSize: '.68rem', color: 'var(--warning, #b7791f)', marginTop: '.25rem' }}>
          Inclua o número no endereço completo. Use s/n quando não houver.
        </span>
      )}
    </div>
  );
}

function CnpjLookupField({ value, onChange, onFound, disabled }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  async function doLookup() {
    const raw = digits(value);
    if (raw.length !== 14) {
      toast.error('CNPJ deve ter 14 dígitos');
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await contactsApi.searchCnpj(raw);
      onFound(result);
      setStatus('ok');
      toast.success('Dados cadastrais preenchidos.');
    } catch (err) {
      setStatus('err');
      toast.error(err.response?.data?.detail || 'CNPJ não encontrado');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: '.375rem' }}>
      <input
        type="text"
        value={maskCnpj(value)}
        onChange={e => {
          onChange(digits(e.target.value).slice(0, 14));
          setStatus(null);
        }}
        className="mono-input"
        placeholder="00.000.000/0000-00"
        maxLength={18}
        style={{ flex: 1 }}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={doLookup}
        disabled={loading || disabled}
        title="Buscar dados públicos pelo CNPJ"
        aria-label="Buscar dados públicos pelo CNPJ"
        style={{
          padding: '.375rem .5rem',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg)',
          cursor: loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: 'var(--text-secondary)',
          flexShrink: 0,
        }}
      >
        {loading
          ? <div className="spinner" style={{ width: 13, height: 13 }} />
          : status === 'ok' ? <CheckCircle2 size={13} style={{ color: 'var(--success)' }} />
          : status === 'err' ? <AlertCircle size={13} style={{ color: 'var(--error)' }} />
          : <Search size={13} />}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 1 — IDENTIFICAÇÃO
// ═══════════════════════════════════════════════════════════════
export function TabIdentificacao() {
  return (
    <div className="form-grid fg-3">
      <FG label="Comarca" req>
        <TxtInput field="comarca" placeholder="Ex: BATATAIS" style={{ textTransform: 'uppercase' }} />
      </FG>
      <FG label="Data da entrevista">
        <DateInput field="interview_date" />
      </FG>
      <FG label="Tipo de ação">
        <Sel field="action_type">
          <option value="Reclamação Trabalhista">Reclamação Trabalhista</option>
          <option value="Ação de Reintegração">Ação de Reintegração</option>
          <option value="Ação de Indenização">Ação de Indenização</option>
          <option value="Mandado de Segurança">Mandado de Segurança</option>
        </Sel>
      </FG>
      <FG label="Entrevistado por" opt>
        <TxtInput field="entrevistado_por" placeholder="Nome de quem atendeu" />
      </FG>
      <FG label="Advogado responsável" opt>
        <LawyerSelect />
      </FG>
      <FG label="Documentos que faltam" opt hint="Ex: CTPS, holerites, termo de rescisão...">
        <TxtInput field="documentos_faltantes" placeholder="Relação de documentos pendentes" />
      </FG>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 2 — RECLAMANTE
// ═══════════════════════════════════════════════════════════════
export function TabReclamante() {
  const { formData, setField, addClaimantPhone, updateClaimantPhone, removeClaimantPhone, lockMode } = useFormStore();
  const phones = formData.claimant_phones || [];
  const lockedPhones = lockMode && isFilledValue(phones);

  const [suggestion, setSuggestion] = useState(null);
  const [showPrefillModal, setShowPrefillModal] = useState(false);

  useEffect(() => {
    const cpf = formData.claimant_cpf || '';
    if (cpf.length !== 11) {
      setSuggestion(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const suggestions = await formsApi.getClaimantSuggestions(
          cpf,
          useFormStore.getState().caseId || ''
        );
        if (suggestions && suggestions.length > 0) {
          setSuggestion(suggestions[0]);
        } else {
          setSuggestion(null);
        }
      } catch (err) {
        setSuggestion(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.claimant_cpf]);

  const handleConfirmPrefill = async (keysToMerge) => {
    if (!suggestion || !suggestion.claimant) return;

    const suggestedClaimant = suggestion.claimant;
    const currentFormData = useFormStore.getState().formData;

    // Monta o objeto com os dados atualizados para salvar
    const updatedFormData = { ...currentFormData };
    keysToMerge.forEach(key => {
      if (key === 'claimant_phones') {
        updatedFormData.claimant_phones = suggestedClaimant.claimant_phones || [];
      } else {
        updatedFormData[key] = suggestedClaimant[key];
      }
    });

    const caseId = useFormStore.getState().caseId;
    if (caseId) {
      try {
        await formsApi.save(caseId, updatedFormData);
      } catch (err) {
        toast.error('Erro ao salvar rascunho da ficha.');
        return; // Interrompe para não alterar o estado local nem mostrar toast de sucesso
      }
    }

    // Se o save funcionou (ou não havia caseId), aplica as alterações no store local
    keysToMerge.forEach(key => {
      if (key === 'claimant_phones') {
        setField('claimant_phones', suggestedClaimant.claimant_phones || []);
      } else {
        setField(key, suggestedClaimant[key]);
      }
    });

    // Registra a auditoria apenas se houver um caseId real
    if (caseId) {
      try {
        await formsApi.acceptClaimantPrefill(
          suggestion.source_case_id,
          caseId,
          keysToMerge
        );
      } catch (err) {
        // Ignora erro do backend de auditoria se não implementado
      }
    }

    toast.success('Dados cadastrais mesclados com sucesso!');
    setShowPrefillModal(false);
    setSuggestion(null);
  };

  return (
    <div className="form-grid fg-2">
      {suggestion && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '.5rem',
          background: 'rgba(200, 144, 42, 0.08)',
          border: '1px solid var(--gold)',
          borderRadius: 'var(--radius-md)',
          padding: '.625rem 1rem',
          marginBottom: '1rem',
          gridColumn: 'span 2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <Sparkles size={16} color="var(--gold)" />
            <span style={{ fontSize: '.8rem', color: 'var(--text-primary)', fontWeight: 500 }}>
              Encontramos dados deste reclamante em outra ficha.
            </span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => setShowPrefillModal(true)}
            style={{ borderColor: 'var(--gold)', color: 'var(--gold-dark)', padding: '.25rem .75rem' }}
          >
            Revisar dados
          </button>
        </div>
      )}

      {showPrefillModal && suggestion && (
        <PrefillMergeModal
          suggestion={suggestion}
          currentFicha={formData}
          onClose={() => setShowPrefillModal(false)}
          onConfirm={handleConfirmPrefill}
        />
      )}

      <SepLabel>Identificação pessoal</SepLabel>

      <FG label="Nome completo" req span="2">
        <TxtInput field="claimant_name" placeholder="Nome conforme RG" style={{ textTransform: 'uppercase' }} />
      </FG>
      <FG label="CPF" req>
        <CpfInput field="claimant_cpf" placeholder="000.000.000-00" />
      </FG>
      <FG label="Data de nascimento" req>
        <DateInput field="claimant_birth_date" />
      </FG>
      <FG label="Gênero" req>
        <Sel field="claimant_gender">
          <option value="">Não informado</option>
          <option value="F">Feminino</option>
          <option value="M">Masculino</option>
        </Sel>
      </FG>
      <FG label="RG">
        <TxtInput field="claimant_rg" mono placeholder="00.000.000-0" />
      </FG>
      <FG label="Órgão expedidor">
        <TxtInput field="claimant_rg_issuer" placeholder="SSP/SP" />
      </FG>
      <FG label="Nome da mãe" span="2">
        <TxtInput field="claimant_mother_name" />
      </FG>
      <FG label="Profissão / ocupação" req>
        <TxtInput field="claimant_occupation" placeholder="Ex: Auxiliar de produção" />
      </FG>
      <FG label="Escolaridade">
        <Sel field="claimant_education">
          <option value="">Selecionar...</option>
          <option value="analfabeto">Analfabeto</option>
          <option value="fundamental_incompleto">Fundamental incompleto</option>
          <option value="fundamental">Fundamental completo</option>
          <option value="medio_incompleto">Médio incompleto</option>
          <option value="medio">Médio completo</option>
          <option value="superior_incompleto">Superior incompleto</option>
          <option value="superior">Superior completo</option>
        </Sel>
      </FG>

      <SepLabel>CTPS e PIS</SepLabel>
      <FG label="CTPS — Número">
        <TxtInput field="claimant_ctps_number" mono placeholder="000000" />
      </FG>
      <FG label="CTPS — Série">
        <TxtInput field="claimant_ctps_serie" mono placeholder="00000" />
      </FG>
      <FG label="CTPS — UF">
        <UFSel field="claimant_ctps_uf" />
      </FG>
      <FG label="PIS / NIT" opt>
        <PisInput field="claimant_pis" placeholder="000.00000.00-0" />
      </FG>

      <SepLabel>Contato</SepLabel>
      <FG label="Telefones" span="2">
        {phones.map((ph) => (
          <div key={ph.id} style={{ display: 'flex', gap: '.375rem', alignItems: 'center', marginBottom: '.5rem' }}>
            <PhoneInput
              value={ph.number}
              onChange={v => updateClaimantPhone(ph.id, { number: v })}
              placeholder="(00) 00000-0000"
              style={{ flex: 1 }}
              disabled={lockedPhones}
            />
            <select
              value={ph.type}
              onChange={e => updateClaimantPhone(ph.id, { type: e.target.value })}
              style={{ width: '6rem', flexShrink: 0 }}
              disabled={lockedPhones}
            >
              <option value="cel">Celular</option>
              <option value="res">Residencial</option>
              <option value="com">Comercial</option>
            </select>
            <label
              title="WhatsApp"
              aria-label="Marcar telefone como WhatsApp"
              style={{
                display: 'flex', alignItems: 'center', cursor: lockedPhones ? 'not-allowed' : 'pointer',
                padding: '.375rem .5rem',
                border: `1px solid ${ph.is_whatsapp ? 'var(--whatsapp)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-md)',
                background: ph.is_whatsapp ? 'rgba(37,211,102,.1)' : 'transparent',
                flexShrink: 0,
              }}
            >
              <input
                type="checkbox"
                checked={!!ph.is_whatsapp}
                onChange={e => updateClaimantPhone(ph.id, { is_whatsapp: e.target.checked })}
                style={{ display: 'none' }}
                disabled={lockedPhones}
              />
              <MessageSquare size={13} style={{ color: ph.is_whatsapp ? 'var(--whatsapp)' : 'var(--text-muted)' }} />
            </label>
            {phones.length > 1 && (
              <button
                type="button"
                onClick={() => removeClaimantPhone(ph.id)}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '.375rem', cursor: lockedPhones ? 'not-allowed' : 'pointer', color: 'var(--error)', display: 'flex', flexShrink: 0 }}
                disabled={lockedPhones}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={addClaimantPhone} className="btn btn-outline btn-sm" style={{ marginTop: '.25rem' }} disabled={lockedPhones}>
          <Plus size={12} /> Adicionar telefone
        </button>
      </FG>
      <FG label="E-mail" opt>
        <TxtInput field="claimant_email" placeholder="email@exemplo.com" />
      </FG>

      <SepLabel>Endereço</SepLabel>
      <FG label="CEP">
        <CepLookupField
          value={formData.claimant_zip || ''}
          onChange={v => setField('claimant_zip', v)}
          disabled={lockMode && isFilledValue(formData.claimant_zip)}
          onFound={({ logradouro, bairro, city, state }) => {
            setField('claimant_address', logradouro || '');
            setField('claimant_neighborhood', bairro || '');
            if (city) setField('claimant_city', city);
            if (state) setField('claimant_state', state);
          }}
        />
      </FG>
      <FG label="Estado">
        <UFSel field="claimant_state" />
      </FG>
      <FG label="Cidade" req>
        <CityAutocompleteField
          value={formData.claimant_city || ''}
          state={formData.claimant_state || ''}
          onChange={v => setField('claimant_city', v)}
          disabled={lockMode && isFilledValue(formData.claimant_city)}
        />
      </FG>
      <FG label="Endereço completo" req span="2" hint="Informe rua e número no mesmo campo; complemento pode ir depois do número.">
        <AddressAutocompleteField
          value={formData.claimant_address || ''}
          city={formData.claimant_city || ''}
          state={formData.claimant_state || ''}
          onChange={v => setField('claimant_address', v)}
          onSelect={({ logradouro, bairro, city, state, zip }) => {
            if (logradouro) setField('claimant_address', logradouro);
            if (bairro) setField('claimant_neighborhood', bairro);
            if (city) setField('claimant_city', city);
            if (state) setField('claimant_state', state);
            if (zip) setField('claimant_zip', zip);
          }}
          disabled={lockMode && isFilledValue(formData.claimant_address)}
        />
      </FG>
      <FG label="Bairro">
        <TxtInput field="claimant_neighborhood" placeholder="Bairro" autoComplete="address-level3" />
      </FG>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 3 — RECLAMADO (N reclamados)
// ═══════════════════════════════════════════════════════════════
function DefendantCard({ def, index, canRemove }) {
  const { updateDefendant, removeDefendant, setPrimaryDefendant, lockMode } = useFormStore();
  const u = (field, value) => updateDefendant(def.id, { [field]: value });

  const isLocked = (val) => lockMode && isFilledValue(val);
  const lockedPhones = lockMode && isFilledValue(def.phones);

  const addPhone = () => {
    const phones = [...(def.phones || []), { id: `dph_${Date.now()}`, number: '', type: 'cel' }];
    updateDefendant(def.id, { phones });
  };
  const updatePhone = (phoneId, updates) => {
    const phones = (def.phones || []).map(p => p.id === phoneId ? { ...p, ...updates } : p);
    updateDefendant(def.id, { phones });
  };
  const removePhone = (phoneId) => {
    const phones = (def.phones || []).filter(p => p.id !== phoneId);
    updateDefendant(def.id, { phones });
  };

  return (
    <div style={{
      border: `1px solid ${def.is_primary ? 'var(--navy)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-lg)',
      padding: '1rem',
      marginBottom: '.75rem',
      background: def.is_primary ? 'rgba(26,35,50,.03)' : 'transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {index + 1}º Reclamado
          </span>
          {def.is_primary && (
            <span style={{ fontSize: '.65rem', padding: '.15rem .4rem', background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius-sm)' }}>
              Principal
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '.375rem' }}>
          {!def.is_primary && (
            <button
              type="button"
              onClick={() => setPrimaryDefendant(def.id)}
              disabled={lockMode}
              title="Definir como principal"
              aria-label="Definir reclamado como principal"
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '.25rem .4rem', cursor: lockMode ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
            >
              <Star size={12} />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={() => removeDefendant(def.id)}
              disabled={lockMode}
              title="Remover reclamado"
              aria-label="Remover reclamado"
              style={{ background: 'none', border: '1px solid var(--border-error)', borderRadius: 'var(--radius-sm)', padding: '.25rem .4rem', cursor: lockMode ? 'not-allowed' : 'pointer', color: 'var(--error)', display: 'flex', alignItems: 'center' }}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {!def.is_primary && (
        <div className="form-group" style={{ marginBottom: '.75rem' }}>
          <label style={{ fontSize: '.75rem' }}>Tipo de relação</label>
          <select value={def.relationship || 'grupo_economico'} onChange={e => u('relationship', e.target.value)} disabled={isLocked(def.relationship)}>
            <option value="grupo_economico">Grupo econômico (CLT art. 2º §2º)</option>
            <option value="responsabilidade_subsidiaria">Responsabilidade subsidiária</option>
            <option value="solidaria">Responsabilidade solidária</option>
          </select>
        </div>
      )}

      <div className="form-grid fg-2" style={{ gap: '.625rem' }}>
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Razão social / Nome <span className="req">*</span></label>
          <input type="text" value={def.name} onChange={e => u('name', e.target.value)} placeholder="Razão social completa" style={{ textTransform: 'uppercase' }} disabled={isLocked(def.name)} />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>CNPJ</label>
          <CnpjLookupField
            value={def.cnpj || ''}
            onChange={v => u('cnpj', v)}
            disabled={isLocked(def.cnpj)}
            onFound={(data) => {
              if (data.name) u('name', data.name);
              if (data.address) u('address', data.address);
              if (data.neighborhood) u('neighborhood', data.neighborhood);
              if (data.cep) u('zip', data.cep);
              if (data.city) u('city', data.city);
              if (data.state) u('state', data.state);
            }}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>CPF <span className="opt">(pessoa física)</span></label>
          <input
            type="text"
            value={maskCpf(def.cpf)}
            onChange={e => u('cpf', digits(e.target.value).slice(0, 11))}
            className="mono-input"
            placeholder="000.000.000-00"
            maxLength={14}
            disabled={isLocked(def.cpf)}
          />
        </div>
        {def.cpf && !def.cnpj && (
          <div className="form-group">
            <label style={{ fontSize: '.75rem' }}>Gênero <span className="opt">(empregador pessoa física)</span></label>
            <select value={def.gender || ''} onChange={e => u('gender', e.target.value)} disabled={isLocked(def.gender)}>
              <option value="">Não informado</option>
              <option value="F">Feminino</option>
              <option value="M">Masculino</option>
            </select>
          </div>
        )}
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>CEI <span className="opt">(atividade rural)</span></label>
          <input
            type="text"
            value={maskCei(def.cei || '')}
            onChange={e => u('cei', digits(e.target.value).slice(0, 12))}
            className="mono-input"
            maxLength={16}
            disabled={isLocked(def.cei)}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>CEP</label>
          <CepLookupField
            value={def.zip || ''}
            onChange={v => u('zip', v)}
            disabled={isLocked(def.zip)}
            onFound={({ logradouro, bairro, city, state }) => {
              u('address', logradouro || '');
              u('neighborhood', bairro || '');
              if (city) u('city', city);
              if (state) u('state', state);
            }}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Estado</label>
          <select value={def.state} onChange={e => u('state', e.target.value)} disabled={isLocked(def.state)} autoComplete="address-level1">
            <option value="">UF</option>
            {UF_LIST.map(uf => <option key={uf} value={uf}>{uf}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Cidade</label>
          <CityAutocompleteField
            value={def.city || ''}
            state={def.state || ''}
            onChange={v => u('city', v)}
            disabled={isLocked(def.city)}
          />
        </div>
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Endereço completo</label>
          <AddressAutocompleteField
            value={def.address || ''}
            city={def.city || ''}
            state={def.state || ''}
            onChange={v => u('address', v)}
            onSelect={({ logradouro, bairro, city, state, zip }) => {
              if (logradouro) u('address', logradouro);
              if (bairro) u('neighborhood', bairro);
              if (city) u('city', city);
              if (state) u('state', state);
              if (zip) u('zip', zip);
            }}
            disabled={isLocked(def.address)}
          />
          <span className="field-hint">Informe rua e número no mesmo campo; complemento pode ir depois do número.</span>
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Bairro</label>
          <input type="text" value={def.neighborhood || ''} onChange={e => u('neighborhood', e.target.value)} disabled={isLocked(def.neighborhood)} autoComplete="address-level3" />
        </div>

        {/* Telefones do reclamado */}
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Telefones <span className="opt">(opcional)</span></label>
          {(def.phones || []).map((ph) => (
            <div key={ph.id} style={{ display: 'flex', gap: '.375rem', alignItems: 'center', marginBottom: '.4rem' }}>
              <PhoneInput
                value={ph.number}
                onChange={v => updatePhone(ph.id, { number: v })}
                placeholder="(00) 00000-0000"
                style={{ flex: 1 }}
                disabled={lockedPhones}
              />
              <select
                value={ph.type}
                onChange={e => updatePhone(ph.id, { type: e.target.value })}
                style={{ width: '6rem', flexShrink: 0 }}
                disabled={lockedPhones}
              >
                <option value="cel">Celular</option>
                <option value="res">Residencial</option>
                <option value="com">Comercial</option>
              </select>
              <button
                type="button"
                onClick={() => removePhone(ph.id)}
                disabled={lockedPhones}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '.3rem', cursor: lockedPhones ? 'not-allowed' : 'pointer', color: 'var(--error)', display: 'flex', flexShrink: 0 }}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addPhone} disabled={lockedPhones} className="btn btn-ghost btn-sm" style={{ marginTop: '.2rem', fontSize: '.72rem' }}>
            <Plus size={11} /> Adicionar telefone
          </button>
        </div>

        {/* Dados extras */}
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Dados extras <span className="opt">(opcional)</span></label>
          <textarea
            value={def.extra_info || ''}
            onChange={e => u('extra_info', e.target.value)}
            rows={2}
            placeholder="Informações adicionais: sócios, procurador, outras filiais..."
            style={{ resize: 'vertical' }}
            disabled={isLocked(def.extra_info)}
          />
        </div>
      </div>
    </div>
  );
}

export function TabReclamado() {
  const { formData, addDefendant, lockMode } = useFormStore();
  const defendants = formData.defendants || [];
  const lockedAdd = lockMode && isFilledValue(defendants);

  return (
    <div>
      {defendants.map((def, idx) => (
        <DefendantCard key={def.id} def={def} index={idx} canRemove={defendants.length > 1} />
      ))}
      <button
        type="button"
        onClick={addDefendant}
        className="btn btn-outline btn-sm"
        style={{ width: '100%', justifyContent: 'center', marginTop: '.25rem' }}
        disabled={lockedAdd}
      >
        <Plus size={13} /> Adicionar reclamado
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 4 — CONTRATO (N contratos fracionados)
// ═══════════════════════════════════════════════════════════════
// Jornada em horas, positiva: o esquema da ficha recusa sinal e letra ("-30", "30h"), e o cálculo
// leria zero calado.
function horasDigitadas(valor) {
  return String(valor ?? '').replace(/[^\d.]/g, '');
}

function ContractCard({ ct, index, defendants, canRemove }) {
  const { formData, updateContract, removeContract, lockMode } = useFormStore();
  const u = (field, value) => updateContract(ct.id, { [field]: value });
  const usaRemuneracaoPorPeriodo = (formData.remuneracao_periodos || []).length > 0;
  const regime = regimeDoContrato(ct);

  const isLocked = (val) => lockMode && isFilledValue(val);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem', marginBottom: '.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem' }}>
        <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {index + 1}º Contrato / Período
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeContract(ct.id)}
            disabled={lockMode}
            title="Remover contrato"
            aria-label="Remover contrato"
            style={{ background: 'none', border: '1px solid var(--border-error)', borderRadius: 'var(--radius-sm)', padding: '.25rem .4rem', cursor: lockMode ? 'not-allowed' : 'pointer', color: 'var(--error)', display: 'flex', alignItems: 'center' }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {defendants.length > 1 && (
        <div className="form-group" style={{ marginBottom: '.75rem' }}>
          <label style={{ fontSize: '.75rem' }}>Reclamado deste contrato</label>
          <select value={ct.defendant_id || ''} onChange={e => u('defendant_id', e.target.value)} disabled={isLocked(ct.defendant_id)}>
            {defendants.map(d => (
              <option key={d.id} value={d.id}>{d.name || `Reclamado ${d.order_num}`}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-grid fg-2" style={{ gap: '.625rem' }}>
        <div className="form-section-label" style={{ marginBottom: 0 }}>Período</div>

        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Data de admissão <span className="req">*</span></label>
          <DateInputRaw value={ct.hire_date} onChange={v => u('hire_date', v)} disabled={isLocked(ct.hire_date)} />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Data de demissão</label>
          <DateInputRaw value={ct.termination_date} onChange={v => u('termination_date', v)} disabled={isLocked(ct.termination_date)} />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Tipo de dispensa <span className="req">*</span></label>
          <select value={ct.dismissal_type} onChange={e => u('dismissal_type', e.target.value)} disabled={isLocked(ct.dismissal_type)}>
            <option value="">Selecionar...</option>
            <option value="sem_justa_causa">Sem justa causa</option>
            <option value="justa_causa">Com justa causa</option>
            <option value="rescisao_indireta">Rescisão indireta (CLT art. 483)</option>
            <option value="pedido_demissao">Pedido de demissão</option>
            <option value="acordo_mutuo">Acordo mútuo (CLT art. 484-A)</option>
            <option value="fim_contrato">Término de contrato a prazo</option>
            <option value="aposentadoria">Aposentadoria</option>
            <option value="ativo">Contrato em curso (sem desligamento)</option>
          </select>
        </div>
        {/* Na rescisão indireta o término é a data da comunicação ao empregador, e o saldo de salário
            contado até ela presumia dias que o reclamante já não trabalhava (relatório do Dr. Lucas
            de 13/10/2026). */}
        {ct.termination_date && (
          <div className="form-group contrato-campo">
            <label>Último dia efetivamente trabalhado <span className="opt">(se antes do término)</span></label>
            <DateInputRaw value={ct.ultimo_dia_trabalhado || ''} onChange={v => u('ultimo_dia_trabalhado', v)} disabled={isLocked(ct.ultimo_dia_trabalhado)} />
          </div>
        )}
        {/* Biênio sem projetar duas vezes (1.26.1, resposta do Dr. Lucas de 29/10/2026): a saída da CTPS já é o
            fim do aviso (OJ 82), e o aviso trabalhado não se projeta. */}
        {['sem_justa_causa', 'rescisao_indireta', 'justa_causa'].includes(ct.dismissal_type) && (
          <div className="form-group contrato-campo">
            <label>Aviso prévio <span className="opt">(se souber)</span></label>
            <select value={ct.aviso_previo_modalidade || ''} onChange={e => u('aviso_previo_modalidade', e.target.value)} disabled={isLocked(ct.aviso_previo_modalidade)}>
              <option value="">Não informado</option>
              <option value="indenizado">Indenizado</option>
              <option value="trabalhado">Trabalhado</option>
            </select>
          </div>
        )}
        {ct.termination_date && (
          <div className="form-group span-2">
            <label className="check-group">
              <input type="checkbox" checked={!!ct.termino_inclui_aviso} onChange={e => u('termino_inclui_aviso', e.target.checked)} disabled={isLocked(ct.termino_inclui_aviso)} />
              <span className="check-label">O término informado já é a data de saída da CTPS ou do TRCT, com o aviso projetado (OJ 82)</span>
            </label>
          </div>
        )}

        {/* Regime e jornada são do CONTRATO: mudam FGTS, aviso, divisor e a hora a partir da qual
            há extra — no caso-piloto (doméstica de 30 horas, relatório do Dr. Lucas de
            23/09/2026) a peça saiu CLT de 44 horas por não haver onde dizer isso. */}
        <div className="form-section-label span-2 contrato-secao-label">Regime e jornada contratual</div>
        <div className="form-group contrato-campo">
          <label htmlFor={`regime-${ct.id}`}>Regime do contrato</label>
          <select
            id={`regime-${ct.id}`}
            value={regime}
            onChange={e => u('regime_contratual', e.target.value)}
            disabled={isLocked(ct.regime_contratual)}
          >
            {REGIMES_CONTRATUAIS.map(r => <option key={r.id || 'clt_urbano'} value={r.id}>{r.label}</option>)}
          </select>
          {!REGIMES_COM_REGRAS.has(regime) && (
            <span className="contrato-regime-aviso" role="note">
              Sem regras próprias no sistema: revise o cálculo e o texto à mão.
            </span>
          )}
        </div>
        <div className="form-group contrato-campo" role="group" aria-labelledby={`jornada-${ct.id}`}>
          <span className="contrato-campo-rotulo" id={`jornada-${ct.id}`}>Jornada contratual</span>
          <div className="contrato-jornada">
            <label>Horas por dia
              <input
                type="number" min="0" step="any" inputMode="decimal" placeholder="8"
                value={ct.jornada_contratual_diaria_horas ?? ''}
                onChange={e => u('jornada_contratual_diaria_horas', horasDigitadas(e.target.value))}
                disabled={isLocked(ct.jornada_contratual_diaria_horas)}
              />
            </label>
            <label>Horas por semana
              <input
                type="number" min="0" step="any" inputMode="decimal" placeholder="44"
                value={ct.jornada_contratual_semanal_horas ?? ''}
                onChange={e => u('jornada_contratual_semanal_horas', horasDigitadas(e.target.value))}
                disabled={isLocked(ct.jornada_contratual_semanal_horas)}
              />
            </label>
          </div>
          <span className="field-hint">
            Deixe em branco para 8h/44h. Define o divisor (30h → 150) e a partir de que hora há hora extra.
          </span>
        </div>

        <div className="form-section-label span-2" style={{ marginBottom: 0 }}>Registro e funções</div>
        <div className="form-group span-2" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <label className="check-group">
            <input type="checkbox" checked={!!ct.registered_in_ctps} onChange={e => u('registered_in_ctps', e.target.checked)} disabled={isLocked(ct.registered_in_ctps)} />
            <span className="check-label">Registrado em CTPS</span>
          </label>
          <label className="check-group">
            <input type="checkbox" checked={!!ct.fgts_deposited} onChange={e => u('fgts_deposited', e.target.checked)} disabled={isLocked(ct.fgts_deposited)} />
            <span className="check-label">FGTS recolhido regularmente</span>
          </label>
        </div>

        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>{ct.registered_in_ctps ? 'Função contratada (CTPS)' : 'Função exercida / contratada'} <span className="req">*</span></label>
          <input type="text" value={ct.contracted_role} onChange={e => u('contracted_role', e.target.value)} placeholder="Ex: Auxiliar de almoxarife" disabled={isLocked(ct.contracted_role)} />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>{ct.registered_in_ctps ? 'Salário registrado (R$)' : 'Salário pactuado (R$)'} <span className="req">*</span></label>
          <CurrencyInputRaw
            value={ct.registered_salary}
            onChange={v => u('registered_salary', v)}
            placeholder="0,00"
            disabled={isLocked(ct.registered_salary) || usaRemuneracaoPorPeriodo}
            title={usaRemuneracaoPorPeriodo ? 'A remuneração por período substitui este valor no cálculo' : undefined}
          />
        </div>
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Funções exercidas de fato</label>
          <textarea value={ct.actual_roles} onChange={e => u('actual_roles', e.target.value)} rows={2} placeholder="Ex: expedição, montagem de peças, operador de empilhadeira..." style={{ resize: 'vertical' }} disabled={isLocked(ct.actual_roles)} />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Salário pago por fora (R$) <span className="opt">(opcional)</span></label>
          <CurrencyInputRaw
            value={ct.off_payroll_salary || ''}
            onChange={v => u('off_payroll_salary', v)}
            placeholder="0,00"
            disabled={isLocked(ct.off_payroll_salary) || usaRemuneracaoPorPeriodo}
            title={usaRemuneracaoPorPeriodo ? 'A remuneração por período substitui este valor no cálculo' : undefined}
          />
        </div>
        {usaRemuneracaoPorPeriodo && (
          <div className="field-hint span-2">
            Os valores únicos ficam somente para compatibilidade; o cálculo usa a remuneração por período abaixo.
          </div>
        )}

        <div className="form-section-label span-2" style={{ marginBottom: 0 }}>Benefícios</div>
        <div className="form-group span-2" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
          {[
            { f: 'transport_benefit', l: 'Vale transporte' },
            { f: 'food_benefit',      l: 'Cesta básica' },
            { f: 'meal_benefit',      l: 'Vale refeição' },
            { f: 'health_plan',       l: 'Plano de saúde' },
          ].map(({ f, l }) => (
            <label key={f} className="check-group">
              <input type="checkbox" checked={!!ct[f]} onChange={e => u(f, e.target.checked)} disabled={isLocked(ct[f])} />
              <span className="check-label">{l}</span>
            </label>
          ))}
        </div>
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Outros benefícios <span className="opt">(opcional)</span></label>
          <input type="text" value={ct.other_benefits || ''} onChange={e => u('other_benefits', e.target.value)} placeholder="Ex: moradia, combustível, uniforme..." disabled={isLocked(ct.other_benefits)} />
        </div>

        <div className="form-section-label span-2" style={{ marginBottom: 0 }}>Rescisão</div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Valor líquido da rescisão (R$) <span className="opt">(opcional)</span></label>
          <CurrencyInputRaw
            value={ct.severance_amount || ''}
            onChange={v => u('severance_amount', v)}
            placeholder="0,00"
            disabled={isLocked(ct.severance_amount)}
          />
        </div>
        <div className="form-group">
          <label style={{ fontSize: '.75rem' }}>Data do pagamento da rescisão <span className="opt">(opcional)</span></label>
          <DateInputRaw value={ct.severance_payment_date} onChange={v => u('severance_payment_date', v)} disabled={isLocked(ct.severance_payment_date)} />
        </div>
        <div className="form-group span-2">
          <label style={{ fontSize: '.75rem' }}>Observações do contrato <span className="opt">(opcional)</span></label>
          <textarea value={ct.notes || ''} onChange={e => u('notes', e.target.value)} rows={2} placeholder="Informações adicionais sobre este período..." style={{ resize: 'vertical' }} disabled={isLocked(ct.notes)} />
        </div>
      </div>
    </div>
  );
}

function dateAsDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000 : null;
}

function remuneracaoTemLacuna(periodos, contrato) {
  if (!periodos.length) return false;
  const admissao = dateAsDay(contrato?.hire_date);
  const demissao = dateAsDay(contrato?.termination_date);
  if (admissao === null || demissao === null) return false;

  let proximoDia = admissao;
  for (const periodo of [...periodos].sort((a, b) => String(a.inicio).localeCompare(String(b.inicio)))) {
    const inicio = dateAsDay(periodo.inicio);
    const fim = dateAsDay(periodo.fim);
    if (inicio === null || inicio > proximoDia) return true;
    if (fim === null || fim >= demissao) return false;
    proximoDia = Math.max(proximoDia, fim + 1);
  }
  return proximoDia <= demissao;
}

// Pedidos que o quinquênio corta (backend `prescricao.PERIODICOS`): são os que uma ação anterior pode ter
// interrompido. Os ids são os do cálculo.
// Tipos de ação anterior que interrompem a prescrição (1.26.1, respostas do Dr. Lucas de 29/10/2026).
const TIPOS_DE_ACAO_ANTERIOR = [
  { id: 'acao_individual', label: 'Reclamação trabalhista' },
  { id: 'protesto_judicial', label: 'Protesto judicial (IRR Tema 170)' },
  { id: 'acao_coletiva', label: 'Ação coletiva do sindicato (OJ 359)' },
];

const PEDIDOS_DA_PRESCRICAO = [
  { id: 'horas_extras', label: 'Horas extras' },
  { id: 'intervalo_intrajornada', label: 'Intrajornada' },
  { id: 'intervalo_interjornada', label: 'Interjornada' },
  { id: 'periculosidade', label: 'Periculosidade' },
  { id: 'insalubridade', label: 'Insalubridade' },
  { id: 'adicional_funcao', label: 'Acúmulo de função' },
  { id: 'hora_percurso', label: 'Percurso' },
  { id: 'fgts_multa40', label: 'FGTS' },
  { id: 'ferias_vencidas', label: 'Férias vencidas' },
];

function AcoesAnteriores() {
  const { formData, setField, lockMode } = useFormStore();
  const acoes = formData.acoes_anteriores || [];

  function atualizar(indice, campo, valor) {
    setField('acoes_anteriores', acoes.map((a, i) => (
      i === indice ? { ...a, [campo]: valor } : a
    )));
  }
  function alternarPedido(indice, pedidoId) {
    const atuais = acoes[indice].pedidos || [];
    atualizar(indice, 'pedidos', atuais.includes(pedidoId)
      ? atuais.filter(p => p !== pedidoId)
      : [...atuais, pedidoId]);
  }
  function adicionar() {
    setField('acoes_anteriores', [
      ...acoes, { numero_processo: '', tipo: 'acao_individual', data_ajuizamento: '', data_transito_julgado: '', pedidos: [] },
    ]);
  }
  // Até a 1.26.0 o campo se chamava `data_encerramento`: a ficha antiga mostra a data gravada e, ao editar,
  // passa a gravar o nome novo.
  function atualizarTransito(indice, valor) {
    setField('acoes_anteriores', acoes.map((a, i) => {
      if (i !== indice) return a;
      const { data_encerramento: _antigo, ...resto } = a;
      return { ...resto, data_transito_julgado: valor };
    }));
  }
  function remover(indice) {
    setField('acoes_anteriores', acoes.filter((_, i) => i !== indice));
  }

  return (
    <div className="period-editor">
      {acoes.length === 0 && (
        <p className="period-editor-help">
          Só se houve reclamação anterior, protesto judicial ou ação coletiva do sindicato com pedidos idênticos:
          ela interrompe a prescrição desses pedidos (art. 11, § 3º, da CLT), uma única vez, e o quinquênio passa
          a contar do ajuizamento dela.
        </p>
      )}
      {acoes.map((acao, i) => (
        <div className="period-editor-card" key={i}>
          <div className="period-editor-grid">
            <label>Número do processo
              <input type="text" value={acao.numero_processo || ''} disabled={lockMode}
                onChange={e => atualizar(i, 'numero_processo', e.target.value)} />
            </label>
            <label>Tipo
              <select value={acao.tipo || 'acao_individual'} disabled={lockMode}
                onChange={e => atualizar(i, 'tipo', e.target.value)}>
                {TIPOS_DE_ACAO_ANTERIOR.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label>Ajuizada em
              <DateInputRaw value={acao.data_ajuizamento} disabled={lockMode}
                onChange={valor => atualizar(i, 'data_ajuizamento', valor)} />
            </label>
            <label>{acao.tipo === 'protesto_judicial' ? 'Trânsito em julgado (o novo biênio conta do ajuizamento do protesto)' : 'Trânsito em julgado'}
              <DateInputRaw value={acao.data_transito_julgado || acao.data_encerramento || ''} disabled={lockMode}
                onChange={valor => atualizarTransito(i, valor)} />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode}
              onClick={() => remover(i)} title="Remover ação anterior">
              <Trash2 size={13} />
            </button>
          </div>
          <div className="period-editor-help">Pedidos idênticos na ação anterior:</div>
          <div className="form-group">
            {PEDIDOS_DA_PRESCRICAO.map(p => (
              <label className="check-group" key={p.id}>
                <input type="checkbox" checked={(acao.pedidos || []).includes(p.id)} disabled={lockMode}
                  onChange={() => alternarPedido(i, p.id)} />
                <span className="check-label">{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm period-editor-add"
        onClick={adicionar} disabled={lockMode}>
        <Plus size={13} /> Adicionar ação anterior
      </button>
    </div>
  );
}

function RemuneracaoPeriodos() {
  const { formData, setField, lockMode } = useFormStore();
  const periodos = formData.remuneracao_periodos || [];
  const contrato = (formData.contracts || [])[0];

  function atualizar(indice, campo, valor) {
    setField('remuneracao_periodos', periodos.map((p, i) => (
      i === indice ? { ...p, [campo]: valor } : p
    )));
  }
  function adicionar() {
    setField('remuneracao_periodos', [
      ...periodos, { inicio: '', fim: '', registrado: '', extrafolha: '' },
    ]);
  }
  function remover(indice) {
    setField('remuneracao_periodos', periodos.filter((_, i) => i !== indice));
  }

  return (
    <div className="period-editor">
      {periodos.length === 0 && (
        <p className="period-editor-help">
          Use quando o salário registrado ou a parcela por fora mudaram durante o contrato.
        </p>
      )}
      {periodos.map((periodo, i) => (
        <div className="period-editor-card" key={i}>
          <div className="period-editor-grid">
            <label>De
              <DateInputRaw value={periodo.inicio} disabled={lockMode}
                onChange={valor => atualizar(i, 'inicio', valor)} />
            </label>
            <label>Até
              <DateInputRaw value={periodo.fim} disabled={lockMode}
                onChange={valor => atualizar(i, 'fim', valor)} />
            </label>
            <label>Registrado (R$)
              <CurrencyInputRaw value={periodo.registrado} disabled={lockMode}
                onChange={valor => atualizar(i, 'registrado', valor)} />
            </label>
            <label>Por fora (R$)
              <CurrencyInputRaw value={periodo.extrafolha} disabled={lockMode}
                onChange={valor => atualizar(i, 'extrafolha', valor)} />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode}
              onClick={() => remover(i)} title="Remover período">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      {remuneracaoTemLacuna(periodos, contrato) && (
        <div className="alert alert-warning period-editor-warning">
          Há intervalo sem remuneração entre a admissão e a rescisão. Esse buraco subestima o FGTS.
        </div>
      )}
      <button type="button" className="btn btn-ghost btn-sm period-editor-add"
        onClick={adicionar} disabled={lockMode}>
        <Plus size={13} /> Adicionar período de remuneração
      </button>
    </div>
  );
}

export function TabContrato() {
  const { formData, addContract, lockMode } = useFormStore();
  const contracts = formData.contracts || [];
  const defendants = formData.defendants || [];
  const lockedAdd = lockMode && isFilledValue(contracts);

  return (
    <div>
      {contracts.map((ct, idx) => (
        <ContractCard key={ct.id} ct={ct} index={idx} defendants={defendants} canRemove={contracts.length > 1} />
      ))}
      <button
        type="button"
        onClick={addContract}
        className="btn btn-outline btn-sm"
        style={{ width: '100%', justifyContent: 'center', marginTop: '.25rem' }}
        disabled={lockedAdd}
      >
        <Plus size={13} /> Adicionar período / contrato fracionado
      </button>

      <SepLabel>Remuneração por período <span className="opt">(opcional)</span></SepLabel>
      <RemuneracaoPeriodos />

      <SepLabel>Verbas rescisórias</SepLabel>
      <div className="form-grid fg-2 period-editor">
        <FG
          label="Períodos de férias vencidos e não gozados"
          hint="Sem este número, o pedido de férias vencidas não será incluído"
        >
          <NumInput field="ferias_vencidas_nao_gozadas" min="0" step="1" placeholder="0" />
        </FG>
      </div>

      <SepLabel>Prescrição</SepLabel>
      <div className="form-grid fg-2 period-editor">
        <FG label="Data do ajuizamento" opt hint="Prevista ou real. Vazia, o quinquênio conta de hoje; o protocolo a preenche">
          <DateInput field="data_ajuizamento" />
        </FG>
        <FG label="Ressalva do biênio" opt hint="Só se o biênio aparece vencido e há interrupção ou suspensão que a ficha não mostra">
          <TA field="prescricao_ressalva" rows={2} placeholder="Ex: protesto interruptivo ajuizado em..." />
        </FG>
      </div>
      <AcoesAnteriores />

      <SepLabel>Informações complementares</SepLabel>
      <div className="form-grid fg-2" style={{ marginTop: '.5rem' }}>
        <FG label="Local da prestação de serviço" opt hint="Pode diferir do local de contratação (casos rurais)">
          <TxtInput field="local_prestacao_servico" placeholder="Ex: Fazenda em Altinópolis/SP" />
        </FG>
        <FG label="Local da contratação" opt>
          <TxtInput field="local_contratacao" placeholder="Ex: Batatais/SP" />
        </FG>
        <FG label="Cultura produzida pela fazenda" opt hint="Casos rurais">
          <TxtInput field="cultura_fazenda" placeholder="Ex: Café, aves, gado..." />
        </FG>
        <FG label="Forma de recebimento do salário" opt>
          <Sel field="forma_recebimento_salario">
            <option value="">Selecionar...</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="deposito">Depósito em conta</option>
          </Sel>
        </FG>
        <div className="form-group">
          <Check field="contrato_experiencia" label="Assinou contrato de experiência" />
        </div>
        {formData.contrato_experiencia && (
          <div className="form-group">
            <Check field="clausula_assecuratoria" label="Com cláusula de rescisão antecipada (art. 481 da CLT)" />
          </div>
        )}
        <div className="form-group">
          <Check field="seguro_desemprego_durante_vinculo" label="Recebeu seguro-desemprego durante o vínculo" />
        </div>
        <div className="form-group span-2">
          <Check field="pedido_reconhecimento_vinculo" label="Pedido de reconhecimento de vínculo de emprego" sub="Quando o contrato não foi registrado em CTPS" />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 5 — JORNADA
// ═══════════════════════════════════════════════════════════════
/**
 * Períodos de jornada do contrato.
 *
 * A ficha guardava uma jornada só, e a jornada que muda no meio do contrato ficava presa nos
 * campos de texto livre — a petição saía com uma tabela só e o cálculo das horas extras
 * subestimava o período novo. Cada período aqui vira uma tabela na peça e uma linha na memória
 * de cálculo.
 */
function PeriodosJornada() {
  const { formData, setField, lockMode } = useFormStore();
  const periodos = formData.jornada_periodos || [];
  const DIAS = [
    { k: 'seg', l: 'S' }, { k: 'ter', l: 'T' }, { k: 'qua', l: 'Q' }, { k: 'qui', l: 'Q' },
    { k: 'sex', l: 'S' }, { k: 'sab', l: 'S' }, { k: 'dom', l: 'D' },
  ];

  function atualizar(indice, campo, valor) {
    setField('jornada_periodos', periodos.map((p, i) => (i === indice ? { ...p, [campo]: valor } : p)));
  }
  function alternarDia(indice, dia) {
    const dias = periodos[indice].work_days || {};
    atualizar(indice, 'work_days', { ...dias, [dia]: !dias[dia] });
  }
  function adicionar() {
    setField('jornada_periodos', [...periodos, {
      inicio: '', fim: '', shift_start: '', shift_end: '', intrajornada_interval_minutes: '',
      work_days: { seg: true, ter: true, qua: true, qui: true, sex: true },
    }]);
  }
  function remover(indice) {
    setField('jornada_periodos', periodos.filter((_, i) => i !== indice));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      {periodos.length === 0 && (
        <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', margin: 0 }}>
          Sem períodos informados, a petição usa o horário padrão abaixo como jornada única de todo
          o contrato.
        </p>
      )}
      {periodos.map((p, i) => (
        <div key={i} style={{ border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '.625rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto', gap: '.375rem', alignItems: 'end' }}>
            <label style={{ fontSize: '.7rem' }}>De
              <input type="date" value={p.inicio || ''} disabled={lockMode}
                onChange={e => atualizar(i, 'inicio', e.target.value)} />
            </label>
            <label style={{ fontSize: '.7rem' }}>Até
              <input type="date" value={p.fim || ''} disabled={lockMode}
                onChange={e => atualizar(i, 'fim', e.target.value)} />
            </label>
            <label style={{ fontSize: '.7rem' }}>Entrada
              <TimeRangeRaw value={p.shift_start} disabled={lockMode}
                onChange={valor => atualizar(i, 'shift_start', valor)} />
            </label>
            <label style={{ fontSize: '.7rem' }}>Saída
              <TimeRangeRaw value={p.shift_end} disabled={lockMode}
                onChange={valor => atualizar(i, 'shift_end', valor)} />
            </label>
            <label style={{ fontSize: '.7rem' }}>Intervalo (horas:minutos)
              <DurationRangeRaw value={p.intrajornada_interval_minutes} disabled={lockMode}
                onChange={valor => atualizar(i, 'intrajornada_interval_minutes', valor)} />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode}
              onClick={() => remover(i)} title="Remover período">
              <Trash2 size={13} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '.25rem', marginTop: '.5rem' }}>
            {DIAS.map(({ k, l }, di) => (
              <button key={k} type="button" disabled={lockMode} onClick={() => alternarDia(i, k)}
                title={k}
                style={{
                  width: '1.6rem', height: '1.6rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: '1px solid var(--border-light)', fontSize: '.68rem',
                  background: (p.work_days || {})[k] ? 'var(--gold)' : 'transparent',
                  color: (p.work_days || {})[k] ? '#fff' : 'var(--text-muted)',
                }}>{l}{di === 3 ? 'i' : ''}</button>
            ))}
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={adicionar} disabled={lockMode}
        style={{ alignSelf: 'flex-start' }}>
        <Plus size={13} /> Adicionar período de jornada
      </button>
    </div>
  );
}

export function TabJornada() {
  const { formData, setField, lockMode } = useFormStore();
  const DAYS = [
    { k: 'seg', l: 'Segunda' }, { k: 'ter', l: 'Terça' }, { k: 'qua', l: 'Quarta' },
    { k: 'qui', l: 'Quinta' }, { k: 'sex', l: 'Sexta' }, { k: 'sab', l: 'Sábado' }, { k: 'dom', l: 'Domingo' },
  ];

  const schedules = formData.work_day_schedules || {};

  function setDaySchedule(day, field, value) {
    const current = schedules[day] || {
      start: formData.shift_start ?? '', end: formData.shift_end ?? '',
      interval: formData.intrajornada_interval_minutes ?? '',
    };
    setField('work_day_schedules', { ...schedules, [day]: { ...current, [field]: value } });
  }
  function resetDaySchedule(day) {
    const next = { ...schedules };
    delete next[day];
    setField('work_day_schedules', next);
  }
  function toggleDay(k) {
    setField('work_days', { ...formData.work_days, [k]: !formData.work_days?.[k] });
    if (formData.work_days?.[k]) resetDaySchedule(k);
  }

  const activeDays = DAYS.filter(d => formData.work_days?.[d.k]);

  return (
    <div className="form-grid fg-2">
      <SepLabel>Dias trabalhados</SepLabel>
      <div className="form-group span-2">
        <label>Dias da semana</label>
        <div style={{ display: 'flex', gap: '.375rem', marginTop: '.375rem', flexWrap: 'wrap' }}>
          {DAYS.map(({ k, l }) => {
            const active = formData.work_days?.[k];
            const hasCustom = !!schedules[k];
            const isDayLocked = lockMode && active;
            return (
              <button key={k} type="button"
                onClick={() => toggleDay(k)}
                disabled={isDayLocked}
                style={{
                  padding: '.3rem .7rem', borderRadius: 'var(--radius-md)', border: '1px solid',
                  borderColor: active ? 'var(--navy)' : 'var(--border)',
                  background: active ? 'var(--navy)' : 'transparent',
                  color: active ? '#fff' : 'var(--text-secondary)',
                  fontSize: '.8rem', fontWeight: active ? 500 : 400,
                  cursor: isDayLocked ? 'not-allowed' : 'pointer', transition: 'all .13s',
                  position: 'relative',
                }}>
                {l}
                {hasCustom && (
                  <span style={{ position: 'absolute', top: 2, right: 3, width: 5, height: 5, borderRadius: '50%', background: 'var(--warning)' }} role="img" aria-label="Horário personalizado" title="Horário personalizado" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <SepLabel>Períodos de jornada <span style={{ fontSize: '.7rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(opcional — use quando a jornada mudou no curso do contrato)</span></SepLabel>
      <div className="form-group span-2">
        <PeriodosJornada />
      </div>

      <SepLabel>Horário padrão</SepLabel>
      <FG label="Entrada" req>
        <TimeRangeInput field="shift_start" />
      </FG>
      <FG label="Saída (horário normal)" req>
        <TimeRangeInput field="shift_end" />
      </FG>
      <FG label="Intervalo (horas:minutos)" hint="Ex.: 01:30 = uma hora e meia. Se variar, preencha também o máximo.">
        <DurationRangeInput field="intrajornada_interval_minutes" />
      </FG>

      {/* Horários personalizados por dia */}
      {activeDays.length > 0 && (
        <>
          <SepLabel>Horário por dia <span style={{ fontSize: '.7rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(opcional — sobrepõe o horário padrão)</span></SepLabel>
          <div className="form-group span-2">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.375rem' }}>
              {activeDays.map(({ k, l }) => {
                const custom = schedules[k] || {};
                const isCustom = !!schedules[k];
                const locked = (field) => lockMode && isFilledValue(custom[field]);
                return (
                  <fieldset key={k} className="day-schedule">
                    <legend>{l}{isCustom ? ' — personalizado' : ' — horário padrão'}</legend>
                    <div className="day-schedule-fields">
                      <label>Entrada
                        <TimeRangeRaw value={custom.start ?? formData.shift_start}
                          onChange={v => setDaySchedule(k, 'start', v)} disabled={locked('start')} />
                      </label>
                      <label>Saída
                        <TimeRangeRaw value={custom.end ?? formData.shift_end}
                          onChange={v => setDaySchedule(k, 'end', v)} disabled={locked('end')} />
                      </label>
                      <label>Intervalo (horas:minutos)
                        <DurationRangeRaw value={custom.interval ?? formData.intrajornada_interval_minutes}
                          onChange={v => setDaySchedule(k, 'interval', v)} disabled={locked('interval')} />
                      </label>
                      <label>Percurso do dia, ida + volta (horas:minutos)
                        <input type="time" value={durationClock(custom.commute_minutes ?? formData.commute_minutes)}
                          disabled={locked('commute_minutes')}
                          onChange={e => setDaySchedule(k, 'commute_minutes', durationMinutes(e.target.value))} />
                      </label>
                    </div>
                    <label>Observações do dia / período (mês e ano)
                      <textarea rows={2} value={custom.observations || ''} disabled={locked('observations')}
                        placeholder="Ex.: de janeiro a junho de 2024. Para calcular períodos distintos, preencha também Períodos de jornada."
                        onChange={e => setDaySchedule(k, 'observations', e.target.value)} />
                    </label>
                    <div className="day-schedule-actions">
                      <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode || isCustom}
                        onClick={() => setDaySchedule(k, 'start', formData.shift_start ?? '')}>
                        Salvar horário padrão neste dia
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={!isCustom || lockMode}
                        onClick={() => resetDaySchedule(k)}>
                        Usar horário padrão
                      </button>
                    </div>
                  </fieldset>
                );
              })}
              <p className="field-hint">Editar um horário personaliza o dia e preserva os demais campos. Só clicar no campo não altera a ficha.</p>
            </div>
          </div>
        </>
      )}
      <FG label="Controle de ponto" req>
        <Sel field="point_control_type">
          <option value="">Selecionar...</option>
          <option value="biometrico">Biométrico (digital)</option>
          <option value="digital">Cartão magnético</option>
          <option value={formData.point_control_type === 'manual_livro' ? 'manual_livro' : 'manual_papel'}>Manual em papel / livro de ponto</option>
          <option value="sem_controle">Sem controle</option>
          <option value="eletronico_outro">Eletrônico (outro)</option>
        </Sel>
      </FG>
      <FG label="Irregularidades no ponto" span="2" hint="Ex: registrava saída e voltava a trabalhar, ponto rasurado...">
        <TA field="point_irregularities" rows={2} placeholder="Descrever..." />
      </FG>

      <SepLabel>Horas extras</SepLabel>
      <FG label="Frequência de horas extras">
        <Sel field="overtime_frequency">
          <option value="">Selecionar...</option>
          <option value="diario">Diariamente</option>
          <option value="quase_diario">Quase diariamente (4-5x/sem)</option>
          <option value="semanal">Algumas vezes (2-3x/sem)</option>
          <option value="quinzenal">1-2 vezes por semana</option>
          <option value="eventual">Eventualmente</option>
        </Sel>
      </FG>
      <FG label="Saída com horas extras (horário)" hint="Horário médio quando havia HE">
        <TimeInput field="overtime_end_time" />
      </FG>
      <FG label="Detalhes das horas extras" opt span="2">
        <TA field="overtime_description" rows={2} placeholder="Ex: 2 a 3 vezes por semana laborava até 19h00..." />
      </FG>

      <SepLabel>Sábados, feriados e noturno</SepLabel>
      <div className="form-group">
        <Check field="saturday_work" label="Trabalhava aos sábados" />
        {formData.saturday_work && (
          <input type="text" value={formData.saturday_schedule || ''} onChange={e => setField('saturday_schedule', e.target.value)} placeholder="Ex: 06h50 às 11h (2x/mês até 13h)" style={{ marginTop: '.375rem' }} />
        )}
      </div>
      <div className="form-group">
        <Check field="worked_holidays" label="Trabalhava em feriados" />
        {formData.worked_holidays && (
          <input type="text" value={formData.holidays_description || ''} onChange={e => setField('holidays_description', e.target.value)} placeholder="Quais feriados?" style={{ marginTop: '.375rem' }} />
        )}
      </div>
      <div className="form-group">
        <Check field="night_shift" label="Trabalho noturno (22h–5h)" sub="Adicional noturno de 20%" />
      </div>

      <SepLabel>Hora de percurso / itinerário</SepLabel>
      <FG label="Tempo total de percurso por dia, ida + volta (min)" opt hint="Soma dos dois trechos. Ex.: 1h de ida e 1h de volta = 120. 0 = sem percurso relevante">
        <NumInput field="commute_minutes" placeholder="0" min="0" max="240" />
      </FG>
      <FG label="Descrição do transporte" opt>
        <TxtInput field="commute_description" placeholder="Ex: ônibus às 6h10, 40min cada sentido..." />
      </FG>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 6 — FATOS ESPECIAIS
// ═══════════════════════════════════════════════════════════════
export function TabFatosEspeciais() {
  const { formData, setField, addAccident, updateAccident, removeAccident, lockMode } = useFormStore();
  const accidents = formData.accidents || [];
  const isLocked = (val) => lockMode && isFilledValue(val);
  const lockedAccidents = lockMode && isFilledValue(accidents);


  return (
    <div className="form-grid fg-2">
      <SepLabel>Insalubridade e periculosidade</SepLabel>
      <div className="form-group">
        <Check field="insalubrity" label="Exposto a agente insalubre" sub="NR-15 — adicional de 10%, 20% ou 40%" />
        {formData.insalubrity && (
          <div style={{ marginTop: '.5rem', display: 'flex', flexDirection: 'column', gap: '.375rem' }}>
            <select value={formData.insalubrity_degree || ''} onChange={e => setField('insalubrity_degree', e.target.value)} disabled={isLocked(formData.insalubrity_degree)}>
              <option value="">Grau...</option>
              <option value="minimo">Mínimo — 10%</option>
              <option value="medio">Médio — 20%</option>
              <option value="maximo">Máximo — 40%</option>
            </select>
            <TxtInput className="special-fact-detail" field="insalubrity_agent" placeholder="Ex: ruído, produtos químicos, calor..." />
          </div>
        )}
      </div>
      <div className="form-group">
        <Check field="hazard_pay" label="Atividade perigosa" sub="NR-16 — adicional de 30%" />
        {formData.hazard_pay && <TxtInput className="special-fact-detail" field="hazard_description" placeholder="Ex: inflamáveis, eletricidade..." />}
      </div>

      <SepLabel>Acidentes de trabalho</SepLabel>
      {accidents.map((acc, idx) => (
        <div key={acc.id} className="span-2" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '.75rem', marginBottom: '.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
            <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Acidente {idx + 1}
            </span>
            <button type="button" onClick={() => removeAccident(acc.id)} disabled={lockMode} style={{ background: 'none', border: 'none', cursor: lockMode ? 'not-allowed' : 'pointer', color: 'var(--error)', display: 'flex', alignItems: 'center' }}>
              <Trash2 size={13} />
            </button>
          </div>
          <div className="form-grid fg-2" style={{ gap: '.5rem' }}>
            <div className="form-group">
              <label style={{ fontSize: '.75rem' }}>Data do acidente</label>
              <DateInputRaw value={acc.date} onChange={v => updateAccident(acc.id, { date: v })} disabled={isLocked(acc.date)} />
            </div>
            <div className="form-group">
              <label style={{ fontSize: '.75rem' }}>Local</label>
              <input type="text" value={acc.location} onChange={e => updateAccident(acc.id, { location: e.target.value })} placeholder="Ex: setor de produção" disabled={isLocked(acc.location)} />
            </div>
            <div className="form-group span-2">
              <label style={{ fontSize: '.75rem' }}>Descrição</label>
              <textarea value={acc.description} onChange={e => updateAccident(acc.id, { description: e.target.value })} rows={2} placeholder="O que aconteceu, lesões, houve CAT?" style={{ resize: 'vertical' }} disabled={isLocked(acc.description)} />
            </div>
          </div>
        </div>
      ))}
      <div className="span-2">
        <button type="button" onClick={addAccident} disabled={lockedAccidents} className="btn btn-outline btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
          <Plus size={13} /> Registrar acidente de trabalho
        </button>
      </div>

      <SepLabel>Doença ocupacional</SepLabel>
      <div className="form-group span-2">
        <Check field="occupational_disease" label="Doença ocupacional / profissional" sub="Equiparada a acidente (Lei 8.213/91)" />
        {formData.occupational_disease && <TA rows={2} className="special-fact-detail" field="disease_description" placeholder="Nome da doença, nexo causal..." />}
      </div>

      <SepLabel>Estabilidade provisória</SepLabel>
      <FG label="Razão de estabilidade" placeholder="Deixar em branco se não houver">
        <Sel field="stability_reason">
          <option value="">Sem estabilidade provisória</option>
          <option value="acidentado">Acidentado / doença ocupacional (Lei 8.213 art. 118)</option>
          <option value="gestante">Gestante (ADCT art. 10)</option>
          <option value="cipa">Membro da CIPA</option>
          <option value="sindical">Dirigente sindical</option>
          <option value="contrato_prazo">Contrato a prazo determinado vigente</option>
        </Sel>
      </FG>

      <SepLabel>Irregularidades na rescisão</SepLabel>
      <div className="form-group">
        <Check field="coercion_at_dismissal" label="Coação ou pressão na rescisão" sub="Obrigado a assinar sob ameaça" />
        {formData.coercion_at_dismissal && <TA rows={2} className="special-fact-detail" field="coercion_description" placeholder="O que foi dito, quem participou, há provas?" />}
      </div>
      <div className="form-group">
        <Check field="irregular_notice" label="Aviso prévio irregular" sub="Retroativo ou não cumprido de fato" />
        {formData.irregular_notice && <TA rows={2} className="special-fact-detail" field="notice_description" placeholder="Descrever a irregularidade..." />}
      </div>
      <div className="form-group span-2">
        <Check field="indirect_dismissal" label="Rescisão indireta (CLT art. 483)" sub="Rescisão por culpa do empregador" />
        {formData.indirect_dismissal && (
          <div style={{ marginTop: '.375rem', display: 'flex', flexDirection: 'column', gap: '.375rem' }}>
            <TA rows={2} className="special-fact-detail" field="indirect_dismissal_reason" placeholder="Ex: não pagamento, assédio, serviços além das forças..." />
            <div>
              <label style={{ fontSize: '.75rem' }}>Data de envio do telegrama</label>
              <DateInputRaw
                value={formData.data_envio_telegrama}
                onChange={v => setField('data_envio_telegrama', v)}
              />
            </div>
          </div>
        )}
      </div>

      <SepLabel>Assédio moral</SepLabel>
      <div className="form-group span-2">
        <Check field="moral_harassment" label="Sofreu assédio moral" />
        {formData.moral_harassment && <TA rows={2} className="special-fact-detail" field="harassment_description" placeholder="Quem praticou, frequência, há testemunhas?" />}
      </div>

      <SepLabel>Observações para a IA</SepLabel>
      <FG label="Observações gerais" opt span="2" placeholder="Informações relevantes que não se encaixaram nos campos acima">
        <TA field="general_observations" rows={3} placeholder="Ex: cliente trabalhou 9 anos, nunca exerceu a função contratada..." />
      </FG>
      <FG label="Instruções especiais para a IA" opt span="2" placeholder="Orienta o agente sobre teses ou ênfases específicas">
        <TA field="ai_instructions" rows={2} placeholder="Ex: enfatizar grupo econômico, priorizar pedido de insalubridade..." />
      </FG>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 7 — JURÍDICO & PEDIDOS
// ═══════════════════════════════════════════════════════════════
const TIPO_PEDIDO_LABELS = {
  horas_extras:   'Horas extras',
  horas_percurso: 'Horas in itinere',
  insalubridade:  'Insalubridade',
  periculosidade: 'Periculosidade',
  rescisao:       'Rescisão',
  danos_morais:   'Danos morais',
};

export function TabJuridico() {
  const { formData, setField, lockMode } = useFormStore();
  const selected = formData.jurisprudencias_selecionadas ?? [];
  const isLocked = (val) => lockMode && isFilledValue(val);

  const { data: jurisprudencias = [], isLoading } = useQuery({
    queryKey: ['jurisprudencias'],
    queryFn:  jurisprudenciasApi.list,
    staleTime: 1000 * 60 * 30,
  });

  function toggleJuris(id) {
    setField(
      'jurisprudencias_selecionadas',
      selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id],
    );
  }

  const groups = jurisprudencias.reduce((acc, j) => {
    const g = j.tipo_pedido || 'outros';
    if (!acc[g]) acc[g] = [];
    acc[g].push(j);
    return acc;
  }, {});

  return (
    <div className="form-grid fg-2">
      <SepLabel>Jurisprudência aplicável</SepLabel>
      <div className="form-group span-2">
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text-muted)', fontSize: '.82rem' }}>
            <div className="spinner" style={{ width: 14, height: 14 }} /> Carregando jurisprudências...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {Object.entries(groups).map(([tipo, items]) => (
              <div key={tipo}>
                <div style={{ fontSize: '.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: '.4rem' }}>
                  {TIPO_PEDIDO_LABELS[tipo] || tipo}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.375rem' }}>
                  {items.map(j => {
                    const checked = selected.includes(j.id);
                    return (
                      <label
                        key={j.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '.625rem', cursor: lockMode && checked ? 'not-allowed' : 'pointer',
                          padding: '.5rem .625rem', borderRadius: 6,
                          background: checked ? 'rgba(200,144,42,.08)' : 'var(--bg)',
                          border: `1px solid ${checked ? 'var(--gold)' : 'var(--border-light)'}`,
                          transition: 'all .15s',
                        }}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleJuris(j.id)} disabled={lockMode && checked} style={{ marginTop: 2, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            <Scale size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4, color: 'var(--gold)' }} />
                            {j.tribunal} — {j.numero}
                            <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '.375rem' }}>{j.tema}</span>
                          </div>
                          <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: '.2rem', lineHeight: 1.45 }}>
                            {j.texto_resumido}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {selected.length > 0 && (
          <div style={{ marginTop: '.75rem', fontSize: '.75rem', color: 'var(--gold)' }}>
            {selected.length} súmula{selected.length > 1 ? 's' : ''} selecionada{selected.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      <SepLabel>Honorários e valores</SepLabel>
      <FG label="Honorários sucumbenciais (%)" hint="Percentual pedido à parte reclamada sobre o proveito econômico">
        <input
          type="number" min={5} max={15} step={1}
          value={formData.honorarios_pct ?? 15}
          onChange={e => setField('honorarios_pct', Number(e.target.value))}
          disabled={isLocked(formData.honorarios_pct)}
        />
      </FG>
      <FG label="Estimativa do valor da causa (R$)" opt hint="Valor aproximado para dimensionar a ação">
        <CurrencyInput field="estimativa_valor_causa" placeholder="0,00" />
      </FG>

      <SepLabel>Requerimentos finais da petição</SepLabel>
      <FG label="Requerimentos finais" opt span="2" hint="Um item por linha. Se preenchido, substitui o fecho padrão; preserve os pedidos de notificação, provas, procedência e justiça gratuita aplicáveis.">
        <textarea
          rows={6}
          value={Array.isArray(formData.requerimentos_finais) ? formData.requerimentos_finais.join('\n') : (formData.requerimentos_finais || '')}
          onChange={e => setField('requerimentos_finais', e.target.value.split('\n'))}
          disabled={isLocked(formData.requerimentos_finais)}
        />
      </FG>

      <SepLabel>Pedidos adicionais</SepLabel>
      <FG label="Pedidos extras / observações jurídicas" opt span="2" hint="Pedidos que não estão previstos nos campos acima">
        <TA field="pedidos_extras" rows={4} placeholder="Ex: requerer devolução do desconto indevido de plano de saúde no valor de R$ 180/mês (24 meses = R$ 4.320)..." />
      </FG>

      <SepLabel>Orientação para a IA</SepLabel>
      <FG label="Dica jurídica para a IA" opt span="2" hint="Teses prioritárias, jurisprudência local relevante, cautelas específicas">
        <TA field="ai_hint_juridico" rows={2} placeholder="Ex: priorizar tese de grupo econômico, mencionar convenção coletiva 2022/2023..." />
      </FG>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 8 — VALORES PEDIDOS
// ═══════════════════════════════════════════════════════════════
const HINT_SUBSIDIARIO = 'Pedido subsidiário (art. 326 do CPC): sai no quadro com valor, fora da soma do valor da causa';

/**
 * Deduções (1.21): o que a reclamada já pagou sai do total e da base dos honorários. No caso-piloto
 * (doméstica, relatório do Dr. Lucas de 23/09/2026) o acerto pago por PIX não tinha onde entrar na
 * tela, e sem ele a peça pediria o valor cheio. Grava o valor POSITIVO ("1191.38") — o backend
 * subtrai o módulo — e nunca como pedido de valor negativo, que o esquema da ficha recusa.
 */
function Deducoes() {
  const { formData, setField, lockMode } = useFormStore();
  const deducoes = Array.isArray(formData.deducoes) ? formData.deducoes : [];

  function atualizar(indice, campo, valor) {
    setField('deducoes', deducoes.map((d, i) => (i === indice ? { ...d, [campo]: valor } : d)));
  }
  function adicionar() {
    setField('deducoes', [...deducoes, { descricao: '', valor: '', data: '' }]);
  }
  function remover(indice) {
    setField('deducoes', deducoes.filter((_, i) => i !== indice));
  }

  return (
    <div className="period-editor deducoes">
      <p className="period-editor-help">
        Valores já pagos pela reclamada (ex.: acerto por PIX) — descontados do total e da base dos honorários.
      </p>
      {deducoes.map((deducao, i) => (
        <div className="period-editor-card" key={i}>
          <div className="period-editor-grid deducao-grid">
            <label>Descrição
              <input type="text" value={deducao.descricao || ''} disabled={lockMode}
                placeholder="Ex.: acerto rescisório pago por PIX"
                onChange={e => atualizar(i, 'descricao', e.target.value)} />
            </label>
            <label>Valor (R$)
              {/* O campo de moeda já descarta tudo que não é dígito ou vírgula: o sinal de menos
                  não entra, e o valor gravado é sempre o positivo. */}
              <CurrencyInputRaw value={deducao.valor || ''} disabled={lockMode} placeholder="0,00"
                onChange={valor => atualizar(i, 'valor', valor)} />
            </label>
            <label>Data <span className="opt">(opcional)</span>
              <DateInputRaw value={deducao.data || ''} disabled={lockMode}
                onChange={valor => atualizar(i, 'data', valor)} />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode}
              onClick={() => remover(i)} title="Remover dedução" aria-label="Remover dedução">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm period-editor-add"
        onClick={adicionar} disabled={lockMode}>
        <Plus size={13} /> Adicionar dedução
      </button>
    </div>
  );
}

// Pedido sem caixa na lista (reembolso de despesas de viagem, pedágio e combustível…): id
// `outros_<nome>`, com descrição e valor, que entra no quadro, no valor da causa e na base dos
// honorários. Antes só entrava pela redação, sem linha garantida (relatório do Dr. Lucas de 13/10/2026).
const PREFIXO_PEDIDO_LIVRE = 'outros_';

function PedidosLivres() {
  const { formData, setField, updatePedido, lockMode } = useFormStore();
  const pedidos = formData.pedidos || {};
  const livres = Object.keys(pedidos).filter(id => id.startsWith(PREFIXO_PEDIDO_LIVRE));

  function adicionar() {
    updatePedido(`${PREFIXO_PEDIDO_LIVRE}${Date.now().toString(36)}`, { enabled: true, descricao: '', valor: '' });
  }
  function remover(id) {
    const { [id]: _removido, ...resto } = pedidos;
    setField('pedidos', resto);
  }

  return (
    <div className="period-editor pedidos-livres">
      <p className="period-editor-help">
        Pedido que não está na lista acima (ex.: reembolso de despesas de viagem). Entra no quadro de pedidos, no valor da causa e na base dos honorários.
      </p>
      {livres.map(id => (
        <div className="period-editor-card" key={id}>
          <div className="period-editor-grid deducao-grid">
            <label>Descrição (como sai na peça)
              <input type="text" value={pedidos[id]?.descricao || ''} disabled={lockMode}
                placeholder="Ex.: reembolso de despesas de viagem (pedágio e combustível)"
                onChange={e => updatePedido(id, { descricao: e.target.value, enabled: true })} />
            </label>
            <label>Valor (R$)
              <CurrencyInputRaw value={pedidos[id]?.valor || ''} disabled={lockMode} placeholder="0,00"
                onChange={valor => updatePedido(id, { valor, enabled: true })} />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={lockMode}
              onClick={() => remover(id)} title="Remover pedido" aria-label="Remover pedido">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm period-editor-add" onClick={adicionar} disabled={lockMode}>
        <Plus size={13} /> Adicionar outro pedido
      </button>
    </div>
  );
}

export function TabValoresPedidos() {
  const { formData, updatePedido, lockMode } = useFormStore();
  const pedidos = formData.pedidos || {};
  const suggested = getSuggestedIds(formData);
  const isLocked = (val) => lockMode && isFilledValue(val);

  const groups = PEDIDOS_DEF.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  function applyAutoSuggestion() {
    let count = 0;
    PEDIDOS_DEF.forEach(p => {
      // Desmarcado de propósito (ou dispensado) é decisão: a sugestão não o religa.
      const decidido = pedidos[p.id]?.enabled === false || !!String(pedidos[p.id]?.dispensa || '').trim();
      if (suggested.has(p.id) && !pedidos[p.id]?.enabled && !decidido) {
        updatePedido(p.id, { enabled: true });
        count++;
      }
    });
    toast.success(count > 0 ? `${count} pedido${count > 1 ? 's' : ''} sugerido${count > 1 ? 's' : ''} selecionado${count > 1 ? 's' : ''}` : 'Pedidos já selecionados');
  }

  const enabledItems = PEDIDOS_DEF.filter(p => pedidos[p.id]?.enabled);
  // Mesma regra do quadro da peça (DocumentPreview e renderizadores do backend), que entrou na
  // 1.21 pelo caso-piloto da doméstica (relatório do Dr. Lucas de 23/09/2026): o subsidiário
  // (art. 326 do CPC) só é apreciado se o principal cair, então sai com valor mas fora da soma — somado,
  // inflaria o valor da causa —; e o que a reclamada já pagou sai do total.
  // Principal desmarcado não vai à peça: o subsidiário dele volta a ser pedido autônomo e soma.
  const ehSubsidiario = (p) => {
    const principal = pedidos[p.id]?.subsidiario_de;
    return !!principal && principal !== p.id && !!pedidos[principal]?.enabled;
  };
  const subsidiarios = enabledItems.filter(ehSubsidiario);
  const somaLivres = Object.keys(pedidos)
    .filter(id => id.startsWith(PREFIXO_PEDIDO_LIVRE) && pedidos[id]?.enabled)
    .reduce((sum, id) => sum + valorDecimal(pedidos[id]?.valor), 0);
  const somaPrincipais = enabledItems
    .filter(p => !ehSubsidiario(p))
    .reduce((sum, p) => sum + valorDecimal(pedidos[p.id]?.valor), 0) + somaLivres;
  const totalDeducoes = (Array.isArray(formData.deducoes) ? formData.deducoes : [])
    .reduce((sum, d) => sum + Math.abs(valorDecimal(d?.valor)), 0);
  const total = Math.round((somaPrincipais - totalDeducoes) * 100) / 100;

  // Principal possível para `pedidoId`: outro pedido marcado que não seja subsidiário dele — senão
  // A e B virariam subsidiários um do outro e nenhum entraria na soma.
  function principaisPossiveis(pedidoId) {
    return enabledItems.filter(o => o.id !== pedidoId && pedidos[o.id]?.subsidiario_de !== pedidoId);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {enabledItems.length} pedido{enabledItems.length !== 1 ? 's' : ''} selecionado{enabledItems.length !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: '.15rem' }}>
            Selecione os pedidos e informe o valor estimado de cada um
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={applyAutoSuggestion}
          disabled={lockMode}
          style={{ display: 'flex', alignItems: 'center', gap: '.375rem' }}
        >
          <TrendingUp size={13} /> Sugestão automática
        </button>
      </div>

      {Object.entries(groups).map(([group, items]) => (
        <div key={group} style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', padding: '.375rem 0', borderBottom: '1px solid var(--border-light)', marginBottom: '.375rem' }}>
            {group}
          </div>
          {items.map(p => {
            const entry = pedidos[p.id] || { enabled: false, valor: '', notas: '' };
            const isSuggested = suggested.has(p.id);
            const rotulo = rotuloDoPedido(p, formData);
            const opcoesPrincipal = entry.enabled ? principaisPossiveis(p.id) : [];
            // Principal gravado que não está entre as opções (desmarcado, ou gravado pelo
            // conector): continua visível no select em vez de parecer "principal" em silêncio.
            const principalForaDasOpcoes = entry.subsidiario_de
              && !opcoesPrincipal.some(o => o.id === entry.subsidiario_de)
              ? PEDIDOS_DEF.find(o => o.id === entry.subsidiario_de) || { id: entry.subsidiario_de, label: entry.subsidiario_de }
              : null;
            return (
              <div
                key={p.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5rem 1fr 6.5rem',
                  gap: '.5rem',
                  alignItems: 'start',
                  padding: '.4rem .5rem',
                  borderRadius: 6,
                  background: entry.enabled ? 'rgba(26,35,50,.03)' : 'transparent',
                  border: `1px solid ${entry.enabled ? 'var(--border)' : 'transparent'}`,
                  marginBottom: '.2rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!entry.enabled}
                  onChange={e => updatePedido(p.id, { enabled: e.target.checked })}
                  disabled={lockMode && entry.enabled}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontSize: '.82rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '.375rem', flexWrap: 'wrap' }}>
                    {rotulo}
                    {isSuggested && (
                      <span style={{ fontSize: '.62rem', padding: '.1rem .3rem', background: 'rgba(200,144,42,.12)', color: 'var(--gold)', borderRadius: 4, fontWeight: 600 }}>
                        sugerido
                      </span>
                    )}
                  </div>
                  {entry.enabled && (
                    <textarea
                      rows={2}
                      aria-label={`Descrição do pedido: ${rotulo}`}
                      value={entry.descricao || ''}
                      onChange={e => updatePedido(p.id, { descricao: e.target.value })}
                      placeholder="Descrição na petição: período, parcela e reflexos"
                      disabled={isLocked(entry.descricao)}
                    />
                  )}
                  {entry.enabled && (
                    <input
                      type="text"
                      value={entry.notas || ''}
                      onChange={e => updatePedido(p.id, { notas: e.target.value })}
                      placeholder="Observação (opcional)..."
                      style={{ marginTop: '.3rem', fontSize: '.75rem', width: '100%' }}
                      disabled={isLocked(entry.notas)}
                    />
                  )}
                  {entry.enabled && (
                    <label className="pedido-subsidiario" title={HINT_SUBSIDIARIO}>
                      Subsidiário de
                      <select
                        aria-label={`Subsidiário de (${rotulo})`}
                        value={entry.subsidiario_de || ''}
                        // Vazio vira ausente (undefined não vai no JSON): o esquema quer o id de
                        // outro pedido ou nada.
                        onChange={e => updatePedido(p.id, { subsidiario_de: e.target.value || undefined })}
                        disabled={isLocked(entry.subsidiario_de)}
                      >
                        <option value="">Nenhum — pedido principal</option>
                        {opcoesPrincipal.map(o => (
                          <option key={o.id} value={o.id}>{rotuloDoPedido(o, formData)}</option>
                        ))}
                        {principalForaDasOpcoes && (
                          <option value={principalForaDasOpcoes.id}>
                            {rotuloDoPedido(principalForaDasOpcoes, formData)}
                            {pedidos[principalForaDasOpcoes.id]?.enabled ? ' (é subsidiário deste pedido)' : ' (não marcado)'}
                          </option>
                        )}
                      </select>
                      {ehSubsidiario(p) && <span className="field-hint">{HINT_SUBSIDIARIO}</span>}
                    </label>
                  )}
                </div>
                <div>
                  {entry.enabled && (
                    <CurrencyInputRaw
                      value={entry.valor || ''}
                      onChange={v => updatePedido(p.id, { valor: v })}
                      placeholder="0,00"
                      disabled={isLocked(entry.valor)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <SepLabel>Outros pedidos</SepLabel>
      <PedidosLivres />

      <SepLabel>Deduções</SepLabel>
      <Deducoes />

      <div style={{
        position: 'sticky', bottom: 0,
        background: 'var(--bg-card, #fff)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '.875rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '.75rem',
        boxShadow: '0 -2px 8px rgba(0,0,0,.06)',
      }}>
        <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
          Estimativa total da causa
          {(subsidiarios.length > 0 || totalDeducoes > 0) && (
            <div className="pedidos-total-detalhe">
              {subsidiarios.length > 0 && (
                `Fora da soma: ${subsidiarios.length} pedido${subsidiarios.length > 1 ? 's' : ''} subsidiário${subsidiarios.length > 1 ? 's' : ''}`
              )}
              {subsidiarios.length > 0 && totalDeducoes > 0 && ' · '}
              {totalDeducoes > 0 && `Deduções: (−) R$ ${formatBRL(String(totalDeducoes))}`}
            </div>
          )}
        </div>
        <div className="pedidos-total-valor" style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono, monospace)' }}>
          {total > 0 ? `R$ ${formatBRL(String(total))}` : '—'}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 9 — PROMPT MASTER
// ═══════════════════════════════════════════════════════════════
/**
 * Petição-modelo do caso: peça já pronta que o escritório indica como espelho.
 *
 * Existe porque os exemplos internos são globais e iguais para todos os casos — não havia como
 * dizer "para este caso, siga aquela peça do arquivo". As seções detectadas orientam quais teses
 * a petição terá, e o trecho de cada seção vira exemplo de forma na redação. Os FATOS continuam
 * vindo só da ficha: a peça-modelo é de outro cliente.
 */
function PeticaoModeloBlock() {
  const caseId = useFormStore.getState().caseId;
  const setField = useFormStore(s => s.setField);
  const [modelo, setModelo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preencher, setPreencher] = useState(true);
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (!caseId) return;
    formsApi.getPeticaoModelo(caseId).then(setModelo).catch(() => setModelo(null));
  }, [caseId]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLoading(true);
    try {
      const data = await formsApi.uploadPeticaoModelo(caseId, file, preencher);
      setModelo(data.modelo);
      const sugerida = data.ficha_json || {};
      const campos = Object.keys(sugerida);
      if (campos.length) {
        // Só preenche o que ainda está vazio: a petição-modelo é de outro cliente e jamais
        // pode sobrescrever um dado que o atendente já colheu deste.
        const atual = useFormStore.getState().formData;
        const aplicados = campos.filter(k => {
          const vazio = atual[k] === undefined || atual[k] === '' || atual[k] === null
            || (Array.isArray(atual[k]) && atual[k].length === 0);
          if (vazio) setField(k, sugerida[k]);
          return vazio;
        });
        toast.success(
          aplicados.length
            ? `Modelo anexado. ${aplicados.length} campo(s) vazio(s) preenchido(s) a partir dele — confira antes de salvar.`
            : 'Modelo anexado. Nenhum campo vazio para preencher a partir dele.'
        );
      } else {
        toast.success('Petição-modelo anexada a este caso.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível ler a petição-modelo.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove() {
    setLoading(true);
    try {
      await formsApi.deletePeticaoModelo(caseId);
      setModelo(null);
      toast.success('Petição-modelo removida.');
    } catch {
      toast.error('Não foi possível remover a petição-modelo.');
    } finally {
      setLoading(false);
    }
  }

  if (!caseId) return null;

  return (
    <div style={{ padding: '.875rem', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.5rem' }}>
        <Scale size={15} style={{ color: 'var(--gold)', flexShrink: 0 }} />
        <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Petição-modelo deste caso
        </div>
      </div>
      <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginBottom: '.75rem' }}>
        Tem uma peça já ajuizada parecida com este caso? Anexe-a e a IA vai seguir a estrutura, as
        teses e o estilo dela. Os fatos continuam vindo apenas desta ficha — o modelo é espelho de
        forma, não fonte de dados do cliente.
      </div>

      {modelo ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-primary)', fontWeight: 600 }}>
              <CheckCircle2 size={13} style={{ verticalAlign: '-2px', marginRight: '.35rem', color: 'var(--success, #2e7d32)' }} />
              {modelo.filename}
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: '.2rem' }}>
              {modelo.secoes?.length
                ? `Seções reconhecidas: ${modelo.secoes.join(', ')}`
                : 'Nenhuma seção numerada reconhecida — o modelo vale só como referência de estilo.'}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleRemove} disabled={loading} style={{ color: 'var(--error)' }}>
            <Trash2 size={13} /> Remover
          </button>
        </div>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: '.6rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={preencher} onChange={e => setPreencher(e.target.checked)} />
            Usar o modelo também para preencher campos vazios da ficha
          </label>
          <input
            ref={inputRef}
            type="file"
            accept=".doc,.docx,.pdf,.txt,.md"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => inputRef.current?.click()} disabled={loading}>
            <Plus size={13} /> {loading ? 'Lendo a peça…' : 'Anexar petição-modelo (DOC, DOCX ou PDF)'}
          </button>
        </>
      )}
    </div>
  );
}

export function TabPromptMaster() {
  const { formData, setField } = useFormStore();

  return (
    <div className="form-grid fg-1" style={{ gap: '1.25rem' }}>
      <PeticaoModeloBlock />
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.625rem .875rem', background: 'rgba(200,144,42,.07)', border: '1px solid rgba(200,144,42,.25)', borderRadius: 8 }}>
        <Bot size={15} style={{ color: 'var(--gold)', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>Prompt Master</div>
          <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: '.1rem' }}>
            Instrução geral complementar somada ao prompt padrão, aos exemplos internos e aos direcionamentos das abas.
            Use para teses específicas, ênfases jurídicas ou preferências de redação deste caso.
          </div>
        </div>
      </div>

      <FG label="Prompt Master (opcional)" opt hint="Complementa o redator da petição; não substitui o prompt padrão do sistema.">
        <textarea
          value={formData.master_prompt ?? ''}
          onChange={e => setField('master_prompt', e.target.value)}
          rows={16}
          style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: '.78rem', lineHeight: 1.6 }}
          placeholder={`Exemplo:\n\nDar ênfase à supressão habitual do intervalo intrajornada e ao dano pela jornada extenuante.\n\nManter estilo formal e objetivo. Quando houver base fática suficiente, relacionar a tese ao entendimento do TST sobre intervalo.`}
        />
      </FG>

      {formData.master_prompt && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
          <span style={{ fontSize: '.73rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
            {formData.master_prompt.length} caracteres
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setField('master_prompt', '')}
            style={{ color: 'var(--error)' }}
          >
            Limpar instrução complementar
          </button>
        </div>
      )}
    </div>
  );
}
